import { ResponsiveSettings } from '../hud/ResponsiveSettings.js';
import {
  AudioEngine,
  DEFAULT_SFX_MANIFEST,
  MusicTrack,
  SfxPlayer,
  type SfxManifest,
  type SfxReport,
} from '../audio/index.js';
import { MusicAnalyser, type MusicLevels } from '../audio/MusicAnalyser.js';

/** What the profiler is fed on frames where no analyser exists yet. */
const SILENT_LEVELS: MusicLevels = { amplitude: 0, bass: 0, mid: 0, treble: 0, onset: 0 };
import {
  AudioProfiler,
  SwitchDirector,
  VisualArtEngine,
  VisualSignalBus,
  applySelection,
  autoRecipe,
  defaultRecipe,
  isInteractionMode,
  layer as visualLayer,
  parseRecipe,
  presetFor,
  randomRecipe,
  randomSeed,
  serializeRecipe,
  AudioFeatureCache,
  ReRenderSession,
  ReplayPlayer,
  ReplayRecorder,
  analysePcm,
  audioHash,
  chartHash,
  featureCacheKey,
  compareReplay,
  parseReplay,
  serializeReplay,
  type AudioFeatureTimeline,
  type AudioProfile,
  type InteractionMode,
  type ReRenderProgress,
  type ReRenderSettings,
  type RenderMode,
  type ReplayComparison,
  type ReplayData,
  type ReplayParseResult,
  type VisualSignalEvent,
  type VisualLayerConfig,
  type VisualRecipe,
  AUDIO_CODECS,
  AvExportSession,
  VIDEO_CODECS,
  describeFailure,
  exportFileName,
  fallbackNotice,
  phaseFor,
  type CodecChoice,
  type ExportConfig,
  type ExportFailureKind,
  type ExportRecipeChoice,
  type AvExportProgress,
  type ReRenderInputs,
  GameplayWaveDirector,
  type GameplayAudioSample,
  parseGameplayWave,
  serialiseGameplayWave,
  type GameplayWaveSettings,
  type GameplayWaveStyle,
  DEFAULT_RECORDING_CONFIG,
  recordingFileName,
  type LiveRecordingConfig,
  type LiveRecordingSession,
  type LiveRecordingStatus,
} from '../visual/index.js';
import { ExportPanel } from '../hud/ExportPanel.js';
import { createAvExport } from '../view/export/createAvExport.js';
import { createLiveRecording } from '../view/record/createLiveRecording.js';
import { probeVideoConfig, webCodecsAvailable } from '../view/export/WebCodecsVideoEncoder.js';
import { audioEncoderAvailable, probeAudioConfig } from '../view/export/WebCodecsAudioEncoder.js';
import { VisualPanel } from '../hud/VisualPanel.js';
import { createRendererFactories } from '../view/visual/createRendererFactories.js';
import { OfflineRenderTarget } from '../view/visual/OfflineRenderTarget.js';
import { decodeToMono } from '../audio/decodeToMono.js';
import { VisualSignalSource } from './VisualSignalSource.js';
import {
  HttpChartGenerationClient,
  SongCreationService,
  isChartGenerationError,
  type ChartGenerationClient,
  type CreatedSong,
} from '../creation/index.js';
import { CreateSongScreen, type CreateSongFields } from '../hud/CreateSongScreen.js';
import { directionFromWire } from '../creation/index.js';
import { JudgePanel } from '../hud/JudgePanel.js';
import { SettingsPanel } from '../hud/SettingsPanel.js';
import { SongInfoPanel } from '../hud/SongInfoPanel.js';
import { ResultScreen } from '../hud/ResultScreen.js';
import { SongSelectScreen, formatBytes } from '../hud/SongSelectScreen.js';
import { TitleScreen } from '../hud/TitleScreen.js';
import { DemoGuide } from '../hud/DemoGuide.js';
import { GameplayStatus } from '../hud/GameplayStatus.js';
import { GameplayDiagnosticsPanel, type GameplayDiagnostics } from '../hud/GameplayDiagnosticsPanel.js';
import { DemoLoader } from './DemoLoader.js';
import { DEMO_TRACKS, nextDemoIndex } from './DemoRegistry.js';
import type { RuntimeMode } from './RuntimeConfig.js';
import {
  IndexedDbSongRepository,
  SongLibrary,
  entryBytes,
  videoMimeFor,
  type SongEntry,
  type SongRepository,
} from '../library/index.js';
import {
  DIFFICULTIES,
  compileSongPackChart,
  copyCoverArrayBuffer,
  copyMusicArrayBuffer,
  isSongPackError,
  isDifficulty,
  readRawChartDocuments,
  type CompiledChart,
  type Difficulty,
  type LoadedSongPack,
} from '../songpack/index.js';
import {
  DEFAULT_JUDGE_SETTINGS,
  Simulation,
  accuracyOf,
  buildPlayResult,
  isRunComplete,
  type PlayResult,
  snapshot,
  type JudgeSettings,
  type SimulationEvent,
  type SimulationSnapshot,
  type SimulationState,
  type TimingGrade,
  type WaveResolvedEvent,
} from '../sim/index.js';
import {
  SettingsStore,
  browserSettingsStorage,
  clampVolume,
  type AudioSettings,
  type SettingsStorage,
} from './SettingsStore.js';
import { WaveViewBinder } from '../view/WaveViewBinder.js';
import { WorldPointer } from '../view/input/WorldPointer.js';
import { RenderingPrototype } from '../view/prototype/RenderingPrototype.js';

/**
 * Phase 1: the bridge from a Unity-exported Song Pack to a running world.
 *
 * ```
 * SongPack v1 (.zip)
 *   -> manifest / charts / music.wav / cover.png
 *   -> Web Audio (decoded, scheduled)
 *   -> displayName -> Mirror Drift -> /video/{displayName}.mp4
 * ```
 *
 * This is the only place that knows about all four layers at once, which is
 * exactly what a runtime is for. Every layer below it stays unaware of the
 * others: `songpack` has no idea audio exists, `audio` has no idea anything is
 * rendered, and `view` has never heard of a Song Pack.
 *
 * Phase 2 adds the simulation to that chain:
 *
 * ```
 * MusicClock.now() -> Simulation.advanceTo() -> WaveViewBinder -> RippleField
 * ```
 *
 * The simulation is advanced from the audio clock and from nothing else, so a
 * dropped frame changes what is *drawn* and never what is *true*.
 *
 * Still no gameplay: waves spawn, grow and retire, but there is no player, no
 * collision and no scoring. Those are Phases 3 and 4.
 */

export interface BridgeSessionOptions {
  readonly canvas: HTMLCanvasElement;
  readonly runtimeMode?: RuntimeMode;
  readonly demoRequested?: boolean;
  readonly demoTrackId?: string;
  /** Dev-server telemetry only. The browser entry never enables this in production. */
  readonly developmentDiagnostics?: boolean;
  /**
   * A Song Pack to seed an empty library with, on first run.
   *
   * Not the song that gets played: the library is the source of songs now, and
   * this is imported into it once, when there is nothing else. Set to `null` to
   * start with an empty library.
   */
  readonly songPackUrl?: string | null;
  /** Where songs are stored. Defaults to IndexedDB. */
  readonly repository?: SongRepository;
  readonly difficulty?: Difficulty;
  /** Instance slots for waves. Also the ripple field's capacity. */
  readonly waveCapacity?: number;
  readonly mirrorCount?: number;
  readonly hudParent?: HTMLElement;
  readonly showStats?: boolean;
  /**
   * Developer view.
   *
   * Off for anybody who just opened the page: the renderer's frame timings,
   * draw counts and the pack's storage id are not information a player can act
   * on, and a screen covered in them reads as an unfinished tool rather than a
   * thing to play. Everything it hides is still one query string away.
   */
  readonly debug?: boolean;
  /**
   * Lights one word per seal, swelling as the best moment approaches.
   *
   * On unless explicitly disabled. Still not a click target — the centre is the
   * only input surface — but it is now load-bearing as a timing cue.
   */
  readonly previewNoteWords?: boolean;
  /** Overrides for the timing windows, the hit radius and the dissolve length. */
  readonly judge?: Partial<JudgeSettings>;
  /**
   * Root seed for the Visual Art Engine.
   *
   * Supply one to reproduce a session's visuals exactly; omit it and a random
   * seed is drawn and reported, so a run that turns out well can be recovered.
   */
  readonly visualSeed?: number;
  /**
   * Sound effects: where the files are, how loud, and which grade fires which.
   *
   * All three are separately overridable so a host can move the files, change
   * the level, or give each grade its own sound without editing this class.
   */
  readonly sfx?: {
    readonly manifest?: SfxManifest;
    readonly volume?: number;
    readonly forGrade?: Readonly<Record<TimingGrade, string | null>>;
  };
  /**
   * Where player settings persist. Defaults to `localStorage`, or to nothing at
   * all where that is blocked — in which case the game simply forgets between
   * sessions rather than failing to start.
   */
  readonly settingsStorage?: SettingsStorage | null;
  /**
   * Starting wave speed. Presentation only; see `Simulation.setWaveSpeed`.
   */
  readonly waveSpeed?: number;
  /**
   * CREATE SONG: where the Python chart engine can be reached, or a client that
   * reaches it some other way.
   *
   * Supplying a `client` is how a desktop build swaps HTTP for IPC without this
   * class changing. `null` disables CREATE SONG entirely — the button is not
   * shown and nothing is constructed.
   */
  readonly chartEngine?:
    | { readonly baseUrl?: string; readonly client?: ChartGenerationClient }
    | null;
}

export interface BridgeDiagnostics {
  readonly stage: BridgeStage;
  readonly gameplay: GameplayDiagnostics;
  readonly rendering: ReturnType<RenderingPrototype['diagnostics']>;
  readonly runtimeMode: RuntimeMode;
  readonly demo: { readonly active: boolean; readonly seed: number; readonly version: number };
  readonly manifest: LoadedSongPack['manifest'] | null;
  readonly difficulty: Difficulty;
  readonly waveSpeed: number;
  readonly chartEvents: number;
  readonly eventsByDifficulty: Record<string, number>;
  readonly audio: {
    readonly state: string;
    readonly durationSeconds: number;
    readonly sampleRate: number;
    readonly channels: number;
    readonly positionSeconds: number;
    readonly outputLatencyMs: number;
    readonly scheduledStart: number | null;
  };
  readonly cover: { readonly present: boolean; readonly width: number; readonly height: number } | null;
  readonly mirror: ReturnType<RenderingPrototype['mirrors']['diagnostics']>;
  /** Wall-clock gap between the audio start call and the mirror start call. */
  readonly startSkewMs: number | null;
  readonly simulation: SimulationSnapshot | null;
  /**
   * For the first handful of events: the time the chart asked for, and the song
   * time the scheduler had actually reached when it fired. The gap should stay
   * within one frame and must not grow.
   */
  readonly spawnAccuracy: readonly {
    readonly eventIndex: number;
    readonly chartTime: number;
    readonly songTime: number;
    readonly deltaMs: number;
  }[];
  readonly settings: AudioSettings;
  readonly sfx: SfxReport;
  /** CREATE SONG: whether it is wired, what it is doing, and where the pack came from. */
  readonly creation: {
    readonly available: boolean;
    readonly phase: string | null;
    readonly packSource: string;
  };
  /** The library, and the object-URL ledger the leak check reads. */
  readonly library: {
    readonly songs: number;
    readonly selectedPackId: string | null;
    readonly loadedPackId: string | null;
    readonly storedBytes: number;
    readonly entries: readonly {
      readonly packId: string;
      readonly displayName: string;
      readonly hasVideo: boolean;
      readonly hasCover: boolean;
      readonly bytes: number;
    }[];
    readonly videoUrls: {
      readonly created: number;
      readonly revoked: number;
      readonly live: number;
    };
  };
  /** Which screen is up, and the frozen result when one is being shown. */
  readonly flow: {
    readonly screen: FlowScreen;
    readonly runComplete: boolean;
    readonly lastEventTime: number;
    readonly result: PlayResult | null;
  };
  readonly visual: {
    readonly interactionMode: InteractionMode;
    readonly engine: ReturnType<VisualArtEngine['diagnostics']>;
    readonly switchDirector: ReturnType<SwitchDirector['diagnostics']>;
    readonly audioProfile: AudioProfile;
  };
  readonly replay: {
    readonly recorder: ReturnType<ReplayRecorder['diagnostics']>;
    readonly replaying: boolean;
    readonly playback: ReturnType<ReplayPlayer['progress']>;
    readonly available: boolean;
    readonly stored: {
      readonly song: string;
      readonly packId: string;
      readonly chartHash: string;
      readonly inputs: number;
      readonly offBeatInputs: number;
      readonly events: number;
      readonly interactionMode: InteractionMode;
      readonly seed: number;
      readonly result: ReplayData['result'];
      readonly bytes: number;
    } | null;
    readonly fidelity: ReplayComparison | null;
  };
  readonly judge: {
    readonly settings: JudgeSettings;
    readonly resolutions: number;
    readonly recent: readonly {
      readonly eventIndex: number;
      readonly grade: string;
      readonly timingOffsetMs: number;
      readonly timingDirection: string;
      readonly tapped: boolean;
    }[];
  };
  readonly error: string | null;
}

/**
 * Wave speed presets, slowest first.
 *
 * Kept here rather than in the gate so the query string, the UI and the session
 * default cannot drift apart, and so moving this into a settings screen later
 * is a change of who reads the list rather than a change of what it is.
 */
export const WAVE_SPEED_PRESETS: readonly number[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

/** Snaps a requested speed to the nearest preset. */
function normaliseWaveSpeed(value: number): number {
  if (!Number.isFinite(value)) return 1;
  let best = WAVE_SPEED_PRESETS[0] as number;
  for (const preset of WAVE_SPEED_PRESETS) {
    if (Math.abs(preset - value) < Math.abs(best - value)) best = preset;
  }
  return best;
}

/**
 * Which effect each grade fires, by logical name.
 *
 * A grade maps to a *name*, never to a file. The judging code says "this was a
 * PERFECT"; this table says a PERFECT sounds like `noteHit`; the manifest says
 * `noteHit` is a particular WAV. Splitting it in three is what lets the sound be
 * replaced by dropping in a file, and lets per-grade effects arrive later by
 * editing one line here rather than by touching the judge.
 *
 * `null` means silence, and both failures are deliberately silent: a badly
 * timed tap leaves the wave standing and a miss was never touched, so neither
 * has anything to confirm.
 */
export const DEFAULT_SFX_FOR_GRADE: Readonly<Record<TimingGrade, string | null>> = Object.freeze({
  perfect: 'noteHit',
  great: 'noteHit',
  good: 'noteHit',
  bad: null,
  miss: null,
});

export type BridgeStage = 'idle' | 'loading' | 'ready' | 'playing' | 'failed';

/**
 * Where the player is in the game, as opposed to what the runtime is doing.
 *
 * Kept separate from `BridgeStage` on purpose. `stage` answers "is a song
 * loaded and running"; this answers "which screen is up". They are related but
 * not the same question — a song is fully loaded and stopped both while its
 * result is being read and while the next one is being chosen — and collapsing
 * them into one enum is how a state machine ends up with `ready` meaning four
 * different things.
 */
export type FlowScreen = 'title' | 'select' | 'playing' | 'result';

/**
 * The difficulties a song actually offers.
 *
 * Version 1 requires all three charts, so "which exist" is never a question of
 * presence — but a chart with no events is a difficulty in name only, and
 * offering it means offering a song that never spawns anything. Those are
 * dropped, unless dropping them would leave nothing at all.
 */
function difficultyChoices(entry: SongEntry): { value: string; label: string; eventCount: number }[] {
  const all = DIFFICULTIES.map((value) => ({
    value,
    label: value.toUpperCase(),
    eventCount: entry.difficulties[value],
  }));
  const playable = all.filter((choice) => choice.eventCount > 0);
  return playable.length > 0 ? playable : all;
}

function describeError(cause: unknown): string {
  if (isSongPackError(cause)) return `${cause.code}: ${cause.message}`;
  if (isChartGenerationError(cause)) return `${cause.code}: ${cause.message}`;
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * A download name Windows will accept, keeping the title readable.
 *
 * Only characters the shell actually rejects are replaced, so Japanese titles
 * and marks like `♡` survive — the same rule Unity's exporter applies, for the
 * same reason: the file should still read as the song.
 */
function safeFileName(displayName: string, fallback: string): string {
  const scrubbed = displayName
    // Exactly the set Windows rejects, plus control characters. Spaces and
    // hyphens are legal and are kept, because replacing them would mangle
    // every real title.
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '_')
    .trim()
    .replace(/[. ]+$/, '')
    .slice(0, 120);
  return `${scrubbed.length > 0 ? scrubbed : fallback.replace(/\.zip$/, '')}.zip`;
}

/** A production export in flight. */
interface VideoExportJob {
  readonly session: AvExportSession;
  cancelled: boolean;
}

/**
 * A yield the browser will not throttle.
 *
 * `setTimeout` is clamped to about a second once a tab stops being visible, and
 * an export that yields with it stops making progress the moment somebody
 * switches windows. A `MessageChannel` message is not clamped, which is what
 * lets a long export keep running in the background and the cancel button keep
 * responding. Established in Phase 7A; reused here rather than re-derived.
 */
function yieldToHost(): Promise<void> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = (): void => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(0);
  });
}

/** Which container a directly-chosen codec implies. */
function containerForCodec(codec: CodecChoice): 'mp4' | 'webm' {
  return codec === 'H.264' ? 'mp4' : 'webm';
}

/**
 * Turns whatever went wrong into something the panel can name.
 *
 * The session reports a message; the panel needs a category, because the
 * category is what decides the advice. Matched on the text the encoders
 * actually produce rather than on a code, since WebCodecs supplies no code.
 */
function classifyFailure(message: string | null): ExportFailureKind {
  if (message === null) return 'unknown';
  const text = message.toLowerCase();
  if (text.includes('audio encode') || text.includes('audio encoder')) return 'audio-encode';
  if (text.includes('muxer') || text.includes('finalize')) return 'mux';
  if (text.includes('encoder')) return 'video-encode';
  if (text.includes('cancel')) return 'cancelled';
  return 'unknown';
}

export class BridgeSession {
  readonly prototype: RenderingPrototype;
  readonly audio: AudioEngine;

  private readonly options: BridgeSessionOptions;
  private readonly panel: SongInfoPanel;
  private readonly select: SongSelectScreen;
  private readonly title: TitleScreen;
  private readonly demoGuide: DemoGuide;
  private readonly gameplayStatus: GameplayStatus;
  private readonly gameplayDiagnosticsPanel: GameplayDiagnosticsPanel | null;
  private schedulerStarted = false;
  private simulationUpdates = 0;
  private lastAudioProgress = 0;
  private audioProgressAt = 0;
  private pageHidden = false;
  private audioLifecycleEpoch = 0;
  private backgroundSuspend: Promise<void> | null = null;
  private readonly onPageHide = (): void => {
    this.pageHidden = true;
    this.pauseGameplayAudio();
  };
  private readonly onPageShow = (): void => {
    this.pageHidden = false;
    if (!document.hidden) void this.resumeGameplayAudio();
  };
  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.pauseGameplayAudio();
    else if (!this.pageHidden) void this.resumeGameplayAudio();
  };
  private demoIndex = 0;
  private readonly demoLoaders = new Map<number, DemoLoader>();
  private demoDifficulty: Difficulty = 'normal';
  private demoSpeed = 1;
  private navigationEpoch = 0;
  private packEpoch = 0;
  private demoArt = false;
  private demoSelectionVersion = 0;
  private demoSfxReady: Promise<void> | null = null;
  private get demoConfig() {
    const track = DEMO_TRACKS[this.demoIndex]!;
    return { ...track, difficulty: this.demoDifficulty, waveSpeed: this.demoSpeed,
      notes: this.demoLoaders.get(this.demoIndex)?.prepared?.song.pack.charts[this.demoDifficulty].events.length ?? track.notes };
  }
  selectDemoTrack(index: number): void {
    if (this.screen !== 'title' || this.demoLoading || !Number.isInteger(index) || !DEMO_TRACKS[index]) return;
    this.demoIndex = index;
    this.title.setDemoSelection(index, this.demoDifficulty);
    for (const key of this.demoLoaders.keys()) if (key !== index) this.demoLoaders.delete(key);
    void this.preloadDemo();
  }
  selectDemoDifficulty(difficulty: Difficulty): void {
    if (this.screen !== 'title' || this.demoLoading || !isDifficulty(difficulty)) return;
    this.demoDifficulty = difficulty;
    this.title.setDemoSelection(this.demoIndex, difficulty);
  }
  setDemoArt(enabled: boolean): void {
    this.demoArt = enabled;
    const control = this.title.element.querySelector<HTMLInputElement>('input[aria-label="Demo Art Mode"]');
    if (control) control.checked = enabled;
    if (this.demoActive) this.setInteractionMode(enabled ? 'ART' : 'SCORE');
  }
  selectDemoSpeed(speed: number): void {
    if (this.screen !== 'title' || this.demoLoading) return;
    this.demoSpeed = normaliseWaveSpeed(speed);
    this.title.setDemoSpeed(this.demoSpeed);
  }
  private get demoLoader() { return this.loaderFor(this.demoIndex); }
  private loaderFor(index: number): DemoLoader {
    let loader = this.demoLoaders.get(index);
    if (!loader) { loader = new DemoLoader(undefined, DEMO_TRACKS[index]!); this.demoLoaders.set(index, loader); }
    return loader;
  }
  private async prepareNextDemo(): Promise<void> {
    const next = nextDemoIndex(this.demoIndex);
    this.result.setNextState('loading');
    try {
      await this.loaderFor(next).preload(bytes => this.audio.decode(bytes));
      if (!this.disposed) this.result.setNextState('ready');
    } catch (error) {
      if (!this.disposed) this.result.setNextState('error', describeError(error));
    }
  }
  async nextDemo(): Promise<boolean> {
    if (!this.demoActive || this.screen !== 'result' || this.demoLoading || this.disposed) return false;
    const next = nextDemoIndex(this.demoIndex);
    if (!this.loaderFor(next).prepared) {
      await this.prepareNextDemo();
      return false; // Fetch/decode never continues into playback without a fresh tap.
    }
    this.demoIndex = next;
    // Bound decoded-audio retention to current + next, not all five full songs.
    for (const index of this.demoLoaders.keys()) if (index !== next) this.demoLoaders.delete(index);
    return this.playDemo();
  }
  private demoActive = false;
  private demoLoading = false;
  private demoPreloadError: string | null = null;
  private demoAudioError: string | null = null;
  private disposed = false;
  private readonly advancedSettings: ResponsiveSettings;
  private customSettings: {
    difficulty: Difficulty;
    waveSpeed: number;
    recipe: VisualRecipe | null;
    interactionMode: InteractionMode;
    gameplay: GameplayWaveSettings;
    autoRecipePending: boolean;
    visualEnabled: boolean;
  } | null = null;
  private readonly result: ResultScreen;
  private readonly library: SongLibrary;
  private readonly backButton: HTMLButtonElement;
  private readonly titleButton: HTMLButtonElement;
  private onKeyDown: ((event: KeyboardEvent) => void) | null = null;
  private screen: FlowScreen = 'title';
  /**
   * The finished run, frozen. Non-null exactly while the Result screen is up.
   */
  private lastResult: PlayResult | null = null;
  /** Song time the last chart event fires at, for the end-of-run check. */
  private lastEventTime = 0;
  /** Set once a run has completed, so completion cannot fire twice. */
  private runComplete = false;
  /** See `BridgeOptions.debug`. */
  private readonly debug: boolean;
  private readonly judgePanel: JudgePanel;
  private readonly sfxPlayer: SfxPlayer;
  private readonly settingsPanel: SettingsPanel;
  private readonly settingsStore: SettingsStore;
  /** Null when CREATE SONG is disabled. Nothing else in the class assumes it. */
  private readonly creation: SongCreationService | null = null;
  private readonly createScreen: CreateSongScreen | null = null;
  /**
   * The last generated pack's charts, as the JSON the pack stores.
   *
   * Kept so REGENERATE SECTION can hand them back and the engine can copy
   * every note outside the section being redone instead of producing it a
   * second time. Null before anything has been generated in this session.
   */
  private baselineCharts: Readonly<Record<string, unknown>> | null = null;
  /** The direction the engine used per difficulty, exactly as it reported it. */
  private resolvedDirections: Readonly<Record<string, Record<string, unknown>>> | null = null;
  /** Where the loaded pack came from, for the diagnostics read-out. */
  private packSource: string;
  private readonly sfxForGrade: Readonly<Record<TimingGrade, string | null>>;
  private readonly pointer: WorldPointer;
  /** Reused per tap so a rapid player allocates nothing. */
  private readonly tapPoint = { x: 0, y: 0 };
  private onPointerDown: ((event: PointerEvent) => void) | null = null;
  /** Every judgement this run, newest last. Kept for the diagnostics read-out. */
  private readonly resolutions: WaveResolvedEvent[] = [];

  private pack: LoadedSongPack | null = null;
  private currentPackId: string | null = null;
  /**
   * Object URL for the playing song's bundled MV.
   *
   * Owned here and nowhere else. Created when a song opens, revoked when it
   * closes — after the video elements have let go of it, never before.
   */
  private packVideoUrl: string | null = null;
  /** Counters for the leak check: these must stay equal after every switch. */
  private createdVideoUrls = 0;
  private revokedVideoUrls = 0;
  private chart: CompiledChart | null = null;
  private currentDifficulty: Difficulty;
  private currentWaveSpeed: number;
  private readonly judgeSettings: JudgeSettings;
  /** The boundary between the rhythm engine and the art engine. */
  private readonly visualBus: VisualSignalBus;
  private readonly visualSource: VisualSignalSource;
  private readonly visualEngine: VisualArtEngine;
  private analyser: MusicAnalyser | null = null;
  private interactionMode: InteractionMode = 'SCORE';
  private readonly visualPanel: VisualPanel;
  private readonly switchDirector = new SwitchDirector({ trigger: 'BAR', every: 4 });
  private readonly profiler = new AudioProfiler();
  /** True while AUTO is waiting to have heard enough to choose a recipe. */
  private autoRecipePending = false;
  /** Last combo seen, so a break can be told from a miss with no streak. */
  private lastCombo = 0;
  private artHandle = 0;

  // --- Replay --------------------------------------------------------------
  private readonly recorder = new ReplayRecorder();
  private readonly replayPlayer: ReplayPlayer;
  /** The last finished run, ready to save. */
  private lastReplay: ReplayData | null = null;
  /** True while a recording is being played rather than a song. */
  private replaying = false;
  /** Signals seen during the current run or replay, for validation. */
  private signalCounts: Record<string, number> = {};
  private replayComparison: ReplayComparison | null = null;

  // --- Offline re-render ------------------------------------------------------
  private readonly featureCache = new AudioFeatureCache();
  private reRender: ReRenderSession | null = null;
  private renderTarget: OfflineRenderTarget | null = null;
  private readonly exportPanel: ExportPanel;
  /** The job in flight, or null. See `startVideoExport`. */
  private videoExport: VideoExportJob | null = null;
  /** The finished file, held until it is saved or replaced. */
  private exportedFile: { readonly bytes: Uint8Array; readonly name: string; readonly mime: string } | null = null;
  private track: MusicTrack | null = null;
  private cover: ImageBitmap | null = null;
  private stage: BridgeStage = 'idle';
  private error: string | null = null;
  private startSkewMs: number | null = null;
  private positionHandle = 0;

  private simulation: Simulation | null = null;
  private binder: WaveViewBinder | null = null;
  /**
   * Which style each gameplay wave wears.
   *
   * Owned by the session rather than the engine because it is not part of the
   * artwork: the background recipe and this answer to different settings, and
   * §23 requires that either can change without disturbing the other.
   */
  private readonly gameplayWaves = new GameplayWaveDirector();
  /**
   * The live capture, when one is running.
   *
   * Deliberately one at a time. Two recorders reading the same canvas is not a
   * useful thing to allow, and the state machine in `LiveRecordingSession`
   * refuses a second start anyway — this field is what stops a second *session*
   * being built in front of it.
   */
  private recording: LiveRecordingSession | null = null;
  private recordingConfig: LiveRecordingConfig = { ...DEFAULT_RECORDING_CONFIG };
  /** The finished capture, held until it is saved or replaced. */
  private recordedFile: { readonly blob: Blob; readonly name: string } | null = null;
  /** Ticks the elapsed counter while recording. */
  private recordingTicker = 0;
  /** Holds the encoded parts; the session never sees a Blob. */
  private recordingAdapter: import('../view/record/MediaRecorderAdapter.js').MediaRecorderAdapter | null = null;
  /** Cached analysis, held only while AUDIO_REACTIVE needs it. */
  private gameplayFeatures: AudioFeatureTimeline | null = null;
  /** Sampled for the first events only; this is a measurement, not a log. */
  private readonly spawnAccuracy: {
    eventIndex: number;
    chartTime: number;
    songTime: number;
    deltaMs: number;
  }[] = [];
  private static readonly SPAWN_SAMPLES = 10;

  constructor(options: BridgeSessionOptions) {
    this.demoIndex = Math.max(0, DEMO_TRACKS.findIndex(track => track.id === options.demoTrackId));
    this.options = options;
    this.packSource = 'library';
    this.currentDifficulty = options.difficulty ?? 'normal';
    this.currentWaveSpeed = normaliseWaveSpeed(options.waveSpeed ?? 1);
    // Resolved once, here, and handed to both the simulation and the renderer.
    // The arc's colours and the drawn hit circle are statements about what the
    // judge will do, so they have to come from the same object the judge uses.
    this.judgeSettings = { ...DEFAULT_JUDGE_SETTINGS, ...options.judge };
    this.audio = new AudioEngine();
    this.prototype = new RenderingPrototype({
      judge: {
        perfectSeconds: this.judgeSettings.perfectSeconds,
        greatSeconds: this.judgeSettings.greatSeconds,
        goodSeconds: this.judgeSettings.goodSeconds,
        noteHitRadius: this.judgeSettings.noteHitRadius,
      },
      canvas: options.canvas,
      // Mirror Drift is loaded here instead, once the manifest supplies a title.
      mirrorCount: options.mirrorCount ?? 6,
      // The session owns the moment of starting, so the mirrors wait for the
      // same gesture that schedules the audio.
      mirrorAutoplay: false,
      // Ripples come from the chart now, not from the prototype's own hashes.
      chartDrivenRipples: true,
      rippleCount: options.waveCapacity ?? 96,
      showStats: options.showStats ?? options.debug === true,
    });
    // --- Visual Art Engine -------------------------------------------------
    //
    // Composed here and nowhere else. The bus is created first because both the
    // source that writes it and the engine that reads it need it, and neither of
    // those two ever meets the other. The engine is handed factories rather than
    // renderers, so this is also the only line in the project where "the art
    // engine" and "three" appear in the same scope.
    this.visualBus = new VisualSignalBus();
    this.visualSource = new VisualSignalSource({
      bus: this.visualBus,
      judge: this.judgeSettings,
    });
    this.visualEngine = new VisualArtEngine({
      bus: this.visualBus,
      factories: createRendererFactories({
        prototype: this.prototype,
        describe: () => ({
          song: this.pack?.manifest.displayName ?? '',
          difficulty: this.currentDifficulty,
        }),
      }),
    });
    this.visualEngine.applyRecipe(defaultRecipe(options.visualSeed ?? randomSeed()));
    // Stepped from the render loop, after the world and before the frame.
    this.prototype.setVisualCallback((_time, delta) => this.stepVisuals(delta));
    // Renderers that write to a texture the frame samples do it here, after
    // the composite. See VisualRenderer.postRender.
    this.prototype.setVisualAfterRender(() => this.visualEngine.postRender());
    // SWITCH mode watches the same bus everything else does, so its triggers are
    // the signals rather than a second notion of where the bars are.
    this.visualBus.subscribe('*', (event) => {
      // Counted for replay validation. Cheap, and it is the only way to compare
      // "what the visuals were told" between a run and its replay.
      this.signalCounts[event.name] = (this.signalCounts[event.name] ?? 0) + 1;

      // During a live run the artwork learns about presses and broken streaks
      // from `handleTap` and `stepSimulation` directly. A replay has neither, so
      // the same two facts are recovered from the signals the player re-emits.
      // Gated on `replaying` because doing it unconditionally would count every
      // live press twice — once from the tap, once from its own signal.
      if (this.replaying) this.feedArtStateFromSignal(event);

      if (this.currentRenderMode !== 'SWITCH') return;
      if (this.switchDirector.handle(event) !== null) this.applySwitchSelection();
    });
    this.replayPlayer = new ReplayPlayer({ bus: this.visualBus });

    this.debug = options.debug === true;

    const parent = options.hudParent ?? document.body;
    /*
     * One column for the two tool panels, anchored to the bottom right and
     * growing upward.
     *
     * They were separately fixed to that corner and overlapped whenever either
     * was open: the export body covered the whole visual panel, so RECORDING's
     * controls — and the VISUAL toggle itself — took clicks that never reached
     * them. Stacking is the fix; opening one still closes the other, so the
     * column is never taller than one body plus two toggles.
     */
    const dock = document.createElement('div');
    dock.className = 'mandalizm-dock';
    // Clear of the bottom bar: the SETTINGS button lives in that corner, and a
    // dock anchored at 16px puts whichever panel is lowest directly on top of
    // it — clicks meant for the panel open Settings, which leaves the run.
    dock.style.cssText =
      'position:fixed;right:16px;bottom:64px;z-index:60;' +
      'width:min(320px,88vw);display:flex;flex-direction:column;gap:8px;align-items:stretch;';
    parent.append(dock);
    this.visualPanel = new VisualPanel(
      {
        // Both panels live in the same corner and either one, open, covers the
        // other completely — so the one being opened closes its neighbour.
        // Without this the recording controls sit under the export body and
        // swallow every click, which looks exactly like a dead button.
        onOpenChanged: (open) => this.onToolPanelToggled('visual', open),
        onInteractionMode: (mode) => this.setInteractionMode(mode),
        onRenderMode: (mode) => this.setRenderMode(mode),
        onSeed: (seed) => this.setVisualSeed(seed),
        onEngineEnabled: (enabled) => this.setVisualEnabled(enabled),
        onLayerEnabled: (id, enabled) => {
          this.visualEngine.updateLayer(id, { enabled });
          this.refreshVisualPanel();
        },
        onLayerOpacity: (id, opacity) => this.visualEngine.updateLayer(id, { opacity }),
        onLayerIntensity: (id, intensity) => this.visualEngine.updateLayer(id, { intensity }),
        onAddLayer: (renderer) => this.addVisualLayer(renderer),
        onRemoveLayer: (id) => this.removeVisualLayer(id),
        onSaveRecipe: () => this.downloadVisualRecipe(),
        onLoadRecipe: (file) => void this.importVisualRecipe(file),
        onRandomize: () => this.randomizeVisualSeed(),
        onSaveReplay: () => this.downloadReplay(),
        onLoadReplay: (file) => void this.importReplay(file),
        onPlayReplay: (recipe) => void this.playReplay({ recipe }),
        onGameplayWave: (settings) => this.setGameplayWaveSettings(settings),
        onRecordingConfig: (config) => this.setRecordingConfig(config),
        onStartRecording: () => void this.startRecording(),
        onStopRecording: () => void this.stopRecording(),
        onCancelRecording: () => void this.cancelRecording(),
        onDownloadRecording: () => this.downloadRecording(),
      },
      dock,
    );
    this.exportPanel = new ExportPanel(
      {
        onOpenChanged: (open) => this.onToolPanelToggled('export', open),
        onStart: (config) => void this.startVideoExport(config),
        onCancel: () => this.cancelVideoExport(),
        onDownload: () => this.downloadExportedVideo(),
        onStill: () => void this.exportStill(),
      },
      dock,
    );
    void this.reportExportCapabilities();
    this.advancedSettings = new ResponsiveSettings([this.visualPanel.element, this.exportPanel.element], dock, () => {
      this.visualPanel.setOpen(false);
      this.exportPanel.setOpen(false);
    }, () => void this.goToTitle());

    this.visualPanel.setAvailableRenderers(this.visualEngine.diagnostics().available);
    this.refreshVisualPanel();
    this.startArtTicker();


    this.panel = new SongInfoPanel(parent, { detailed: options.debug === true });
    this.library = new SongLibrary(options.repository ?? new IndexedDbSongRepository());
    this.select = new SongSelectScreen(
      {
        onSelect: (packId) => this.previewSong(packId),
        onPlay: async (packId) => {
          await this.playSong(packId);
        },
        onDifficulty: (value) => {
          if (isDifficulty(value)) this.selectDifficulty(value);
        },
        onWaveSpeed: (value) => {
          this.selectWaveSpeed(value);
        },
        onTitle: () => void this.goToTitle(),
        onCreate: () => this.createScreen?.setOpen(true),
        onImport: (file) => this.importSongPack(file),
        onExport: (packId) => this.exportSongPack(packId),
        onDelete: (packId) => this.deleteSong(packId),
        onSettings: () => this.settingsPanel.setOpen(true),
      },
      parent,
    );
    this.title = new TitleScreen(
      {
        onDemo: async () => { await this.playDemo(); },
        onDemoTrack: index => this.selectDemoTrack(index),
        onDemoDifficulty: difficulty => this.selectDemoDifficulty(difficulty),
        onDemoSpeed: speed => this.selectDemoSpeed(speed),
        onDemoArt: enabled => this.setDemoArt(enabled),
        onPlay: () => void this.goToSelect(),
        // The same screens the Song Select bottom bar opens. Nothing about
        // creating, importing or configuring exists twice.
        onCreate: () => this.createScreen?.setOpen(true),
        onImport: (file) => this.importSongPack(file),
        onSettings: () => this.settingsPanel.setOpen(true),
      },
      parent,
    );
    this.title.setPlayables(options.runtimeMode === 'playables');
    this.title.setDemoSelection(this.demoIndex, this.demoDifficulty);
    this.select.setPlayables(options.runtimeMode === 'playables');
    this.demoGuide = new DemoGuide(parent);
    this.gameplayStatus = new GameplayStatus(() => { void this.resumeGameplayAudio(true); }, parent);
    this.gameplayDiagnosticsPanel = options.developmentDiagnostics === true
      ? new GameplayDiagnosticsPanel(parent) : null;
    this.result = new ResultScreen(
      {
        onRetry: async () => {
          await this.retry();
        },
        onNext: () => this.nextDemo().then(() => {}),
        onDemoSelect: () => this.goToDemoSelect(),
        onSongSelect: () => this.goToSelect(),
        onTitle: () => this.goToTitle(),
      },
      parent,
    );

    // Reachable while a song is playing, which is what makes A -> B -> A a
    // navigation rather than a page reload.
    this.backButton = document.createElement('button');
    this.backButton.className = 'mandalizm-settings-toggle';
    this.backButton.style.right = '132px';
    this.backButton.type = 'button';
    this.backButton.textContent = 'SONG SELECT';
    this.backButton.hidden = true;
    this.backButton.addEventListener('click', () => void this.returnToSelect());
    parent.appendChild(this.backButton);
    this.titleButton = document.createElement('button');
    this.titleButton.type = 'button';
    this.titleButton.className = 'mandalizm-title-return';
    this.titleButton.textContent = 'TITLE';
    this.titleButton.style.cssText = 'position:fixed;left:max(10px,env(safe-area-inset-left));top:max(10px,env(safe-area-inset-top));z-index:100;padding:8px 12px;min-height:36px;color:#cdfaf3;background:#082b33;border:1px solid #377b80;border-radius:6px;cursor:pointer;font:11px ui-monospace,monospace;';
    this.titleButton.addEventListener('click', () => void this.goToTitle());
    parent.appendChild(this.titleButton);
    this.judgePanel = new JudgePanel(parent);
    if (options.sfx?.volume !== undefined) this.audio.sfxVolume = options.sfx.volume;
    this.sfxForGrade = options.sfx?.forGrade ?? DEFAULT_SFX_FOR_GRADE;
    // Restored before anything can be heard, so the first note of the song
    // already plays at the level the player last chose.
    this.settingsStore = new SettingsStore(
      options.settingsStorage === undefined ? browserSettingsStorage() : options.settingsStorage,
    );
    const restored = this.settingsStore.load({
      musicVolume: this.audio.musicVolume,
      sfxVolume: this.audio.sfxVolume,
    });
    this.audio.musicVolume = restored.musicVolume;
    this.audio.sfxVolume = restored.sfxVolume;

    this.sfxPlayer = new SfxPlayer({
      context: this.audio.context,
      // Its own bus, so the effects level moves without touching the music and
      // nothing here can reach the node MusicTrack plays through.
      destination: this.audio.sfx,
      manifest: options.sfx?.manifest ?? DEFAULT_SFX_MANIFEST,
    });
    this.settingsPanel = new SettingsPanel({
      parent,
      // A list, not two fields: wave speed and the rest arrive as entries here.
      sliders: [
        {
          id: 'music',
          label: 'MUSIC VOLUME',
          read: () => this.audio.musicVolume,
          // Straight to the gain node. Nothing is re-scheduled, no source is
          // touched, and `MusicClock` never learns this happened.
          write: (value) => { this.audio.musicVolume = value; },
        },
        {
          id: 'sfx',
          label: 'SFX VOLUME',
          read: () => this.audio.sfxVolume,
          write: (value) => { this.audio.sfxVolume = value; },
        },
      ],
      actions: [
        { id: 'demo-art-on', label: 'DEMO ART MODE ON', run: () => this.setDemoArt(true) },
        { id: 'demo-art-off', label: 'DEMO ART MODE OFF', run: () => this.setDemoArt(false) },
        {
          id: 'test-sfx',
          label: 'TEST SFX',
          // The logical name, not the judging path: this is for setting a level,
          // and routing it through a judgement would need a wave to exist.
          run: () => { this.sfxPlayer.play('noteHit'); },
        },
      ],
      onChange: () => this.persistSettings(),
    });

    if (options.chartEngine !== null && options.runtimeMode !== 'playables') {
      // The client is an interface here on purpose: this class never learns the
      // engine has a URL, so a desktop shell can hand it an IPC implementation
      // and nothing below changes.
      const client =
        options.chartEngine?.client ??
        new HttpChartGenerationClient(
          options.chartEngine?.baseUrl === undefined
            ? {}
            : { baseUrl: options.chartEngine.baseUrl },
        );
      this.creation = new SongCreationService({
        client,
        // The session already owns an AudioContext; giving the creation layer
        // its own would mean two decoders and a second device open for nothing.
        decode: (data) => this.audio.decode(data),
      });
      this.createScreen = new CreateSongScreen({
        onTitle: () => void this.goToTitle(),
        parent,
        onGenerate: (fields) => this.generateSong(fields),
        // The same call. REGENERATE differs only in what the fields say, and
        // in the audio already sitting in the picker, which is re-sent: the
        // engine keeps nothing between requests.
        onRegenerate: (fields) => this.generateSong(fields),
        // One section at a time. The engine is handed the direction the pack
        // was built with as well as the new one, and returns a chart whose
        // other sections are the ones the player already has.
        onRegenerateSection: (index, fields) => this.generateSong(fields, index),
        onCancel: () => {
          this.creation?.cancel();
        },
        onClose: () => this.createScreen?.setOpen(false),
      });
    }

    this.pointer = new WorldPointer(options.canvas, this.prototype.stage.camera);
    this.attachInput(options.canvas);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private pauseGameplayAudio(): void {
    this.audioLifecycleEpoch += 1;
    if (this.disposed) return;
    if (this.stage !== 'playing' && this.stage !== 'loading') return;
    // Audio and chart time share a clock: suspend the graph, not just rendering.
    this.backgroundSuspend ??= this.audio.suspend();
    this.gameplayStatus.show('Audio paused while the page was in the background.');
  }

  private async resumeGameplayAudio(fromGesture = false): Promise<void> {
    if (this.stage !== 'playing' || this.disposed || this.pageHidden || document.hidden) return;
    const epoch = this.audioLifecycleEpoch;
    // Unlock immediately in the RESUME AUDIO gesture, never after an await.
    const unlocked = fromGesture ? this.audio.resumeFromGesture() : null;
    const suspension = this.backgroundSuspend;
    if (suspension !== null) await suspension;
    if (this.backgroundSuspend === suspension) this.backgroundSuspend = null;
    if (unlocked === null && (this.pageHidden || document.hidden || epoch !== this.audioLifecycleEpoch)) return;
    const resumed = await (unlocked ?? this.audio.resume());
    if (this.disposed) return;
    if (this.pageHidden || document.hidden) {
      // A newer hide may have happened while resume was pending. Reconcile the
      // actual graph with that newest lifecycle state before returning.
      await this.audio.suspend();
      return;
    }
    if (this.stage !== 'playing' || epoch !== this.audioLifecycleEpoch) return;
    if (!resumed || !this.audio.running) {
      this.gameplayStatus.show('The browser paused audio. Tap RESUME AUDIO to continue.');
      return;
    }
    this.audioProgressAt = performance.now();
    this.lastAudioProgress = this.audio.context.currentTime;
    this.prototype.start();
    this.gameplayStatus.hide();
  }

  private gameplayDiagnostics(): GameplayDiagnostics {
    const view = this.prototype.diagnostics();
    return {
      platform: this.audio.platform,
      'audioContext.state': this.audio.state,
      audioStartRequested: this.track?.audioStartRequested ?? false,
      audioSourceStarted: this.track?.audioSourceStarted ?? false,
      audioCurrentTime: this.track?.playbackTime ?? 0,
      gain: this.audio.musicVolume * this.audio.master.gain.value,
      muted: this.audio.musicVolume * this.audio.master.gain.value === 0,
      audioError: this.track?.audioError ?? this.audio.audioError ?? this.demoAudioError ?? this.demoPreloadError,
      audioSessionType: this.audio.audioSessionType,
      demoPreloaded: this.demoLoader.prepared !== null,
      outputSignalPeak: this.audio.outputSignalPeak(),
      audioState: this.audio.state,
      playbackTime: this.audio.clock.elapsed(),
      schedulerStarted: this.schedulerStarted,
      chartLoaded: this.chart !== null,
      currentNoteIndex: this.simulation?.state.consumedEvents ?? 0,
      spawnedNotes: this.simulation?.state.spawnedWaves ?? 0,
      viewportWidth: view.viewport.width,
      viewportHeight: view.viewport.height,
      DPR: view.viewport.devicePixelRatio,
      orientation: view.viewport.orientation,
      updateCount: this.simulationUpdates,
      frameCount: view.frameCount,
      renderedNotes: view.renderedNotes,
    };
  }

  private checkGameplayProgress(): void {
    if (this.stage !== 'playing' || this.pageHidden || document.hidden) return;
    const view = this.prototype.diagnostics();
    if (view.contextLost) {
      this.backgroundSuspend ??= this.audio.suspend();
      this.gameplayStatus.show('Graphics were interrupted. Waiting for the browser to restore the game.', false);
      return;
    }
    if (this.audio.running && this.audio.context.currentTime > this.lastAudioProgress) {
      this.lastAudioProgress = this.audio.context.currentTime;
      this.audioProgressAt = performance.now();
      this.gameplayStatus.hide();
    } else if (performance.now() - this.audioProgressAt > 2500) {
      this.gameplayStatus.show('Audio has not started or was interrupted. Tap RESUME AUDIO to continue.');
    }
    // Re-arm a live owner only. An explicitly stopped offline render stays stopped.
    if (view.requestedRunning && !view.running) this.prototype.start();
  }

  /** Current audio levels, 0..1. */
  get audioSettings(): AudioSettings {
    return { musicVolume: this.audio.musicVolume, sfxVolume: this.audio.sfxVolume };
  }

  /** Opens or closes the settings overlay. Safe at any stage. */
  setSettingsOpen(open: boolean): void {
    this.settingsPanel.setOpen(open);
  }

  /**
   * The CREATE SONG screen, or null when the chart engine is disabled.
   *
   * Exposed so the automated browser check can fill the form and press the real
   * GENERATE button. There is no parallel entry point: what the check exercises
   * is the same screen a player uses.
   */
  get createSong(): CreateSongScreen | null {
    return this.createScreen;
  }

  private persistSettings(): void {
    this.settingsStore.save({
      musicVolume: clampVolume(this.audio.musicVolume, 0),
      sfxVolume: clampVolume(this.audio.sfxVolume, 0),
    });
  }

  /**
   * Binds the tap.
   *
   * `pointerdown` rather than `click` or `mousedown`, for two reasons that both
   * matter here. It is one event for mouse, pen and touch, so the rule "tap the
   * centre" needs no per-device branch. And it fires at press rather than at
   * release, which is the moment the player believes they acted — grading on
   * release would add the length of their own press to every offset.
   *
   * The song time is read *first*, before any geometry: whatever else this
   * handler does, the number it grades against is the clock reading from the
   * instant the input arrived.
   */
  private attachInput(canvas: HTMLCanvasElement): void {
    this.onPointerDown = (event: PointerEvent) => {
      // Primary button only, so a right-click or a middle-click cannot score.
      if (event.button !== 0) return;
      if (this.stage !== 'playing') return;
      if (!this.audio.running || !this.schedulerStarted || this.pageHidden || document.hidden) return;
      const songTime = this.audio.clock.now();
      const point = this.pointer.toWorld(event.clientX, event.clientY, this.tapPoint);
      if (point === null) return;
      this.handleTap(point.x, point.y, songTime);
    };
    canvas.addEventListener('pointerdown', this.onPointerDown);
    // Stops a touch drag from scrolling the page out from under the game.
    canvas.style.touchAction = 'none';
  }

  /**
   * One tap: hit test, judge, and hand the result on.
   *
   * The whole gameplay decision lives in the simulation. This method converts
   * nothing and decides nothing — it passes a world point and a song time in,
   * and distributes what comes back. That is what lets the same judging run
   * headless in a test with no canvas and no camera.
   */
  private handleTap(worldX: number, worldY: number, songTime: number): WaveResolvedEvent | null {
    const simulation = this.simulation;
    if (simulation === null) return null;
    const resolved = simulation.tap(worldX, worldY, songTime);
    // Every press reaches the artwork, including the ones that judged nothing —
    // in ART mode a tap into empty space is still the player touching the piece.
    // `beatDistance` is how far off the grid it landed, which is what separates
    // a steady late player from an erratic accurate one.
    const beatDistance = this.visualSource.distanceToBeat(songTime);
    this.visualSource.publishInput(songTime, worldX, worldY, resolved !== null, beatDistance);
    this.visualEngine.registerInput(songTime, resolved !== null, beatDistance);

    // Recorded here rather than from the bus: this is the press itself, before
    // any mapping has decided what it means, which is what makes the recording
    // reinterpretable later.
    this.recorder.recordInput({
      time: songTime,
      inputType: resolved === null ? 'offBeat' : 'note',
      phase: 'press',
      x: worldX,
      y: worldY,
      strength: 1,
      judged: resolved !== null,
      beatDistance,
    });
    // A tap that lands on no wave is not a judgement. It is the player missing
    // the target entirely, and under this rule that costs nothing at all.
    if (resolved === null) return null;
    // Sound first, HUD second. The effect is the confirmation the player is
    // waiting on, and the panel update is a DOM write they will read later.
    this.playJudgementSfx(resolved.grade);
    this.publishJudgement(resolved);
    return resolved;
  }

  /**
   * Fires the effect for a grade, if that grade has one.
   *
   * Reached only from a resolution, so it cannot sound before the judgement it
   * is reporting. Misses go through `publishJudgement` from the frame loop and
   * never come here at all.
   */
  private playJudgementSfx(grade: TimingGrade): void {
    const name = this.sfxForGrade[grade];
    if (name !== null) this.sfxPlayer.play(name);
  }

  private publishJudgement(resolved: WaveResolvedEvent): void {
    this.resolutions.push(resolved);
    // Bounded: a full hard chart is 508 of these and nothing consumes them yet.
    if (this.resolutions.length > 600) this.resolutions.shift();
    const state = this.simulation?.state;
    this.judgePanel.setJudgement({
      grade: resolved.grade,
      timingOffsetMs: resolved.timingOffsetMs,
      timingDirection: resolved.timingDirection,
      tapped: resolved.tapped,
    });
    if (state !== undefined) {
      this.judgePanel.setScore(state.score, state.combo);
      this.judgePanel.setCounts(state.judgeCounts, state.earlyCount, state.lateCount, state.maxCombo);
    }
  }

  /** The chart currently loaded. `?difficulty=` only supplies the initial value. */
  get difficulty(): Difficulty {
    return this.currentDifficulty;
  }

  /**
   * Chooses which chart the next PLAY will use, or swaps the loaded one.
   *
   * Before a song is opened this only records the choice — Song Select is
   * showing counts taken from the library index, and nothing has been read yet.
   * With a song open, the pack already holds all three charts, so the decoded
   * audio, the cover and the mirrors are untouched and only the chart and
   * everything derived from it are rebuilt.
   *
   * Refused once the song is playing. Replacing a chart mid-song would leave
   * `MusicClock` running against a cursor that had consumed a different set of
   * events, and there is no sane answer for what should already have spawned.
   */
  selectDifficulty(difficulty: Difficulty): boolean {
    if (this.stage === 'playing' || this.stage === 'failed') return false;
    if (difficulty === this.currentDifficulty) return true;
    this.currentDifficulty = difficulty;
    if (this.pack !== null) this.rebuildChart(this.pack);
    this.select.setDifficulty(difficulty);
    return true;
  }

  get waveSpeed(): number {
    return this.currentWaveSpeed;
  }

  /**
   * Sets the wave speed.
   *
   * Refused once the song is playing, for the same reason the difficulty is:
   * every live wave would jump to a new size and a new lead mid-flight. The
   * simulation itself can take the change at any time — that restriction is a
   * play decision, not a technical one.
   */
  selectWaveSpeed(multiplier: number): boolean {
    if (this.stage === 'playing' || this.stage === 'failed') return false;
    const next = normaliseWaveSpeed(multiplier);
    if (next === this.currentWaveSpeed) return true;
    this.currentWaveSpeed = next;
    this.simulation?.setWaveSpeed(next);
    if (this.pack !== null) this.rebuildChart(this.pack);
    this.select.setWaveSpeed(next);
    return true;
  }

  /**
   * Builds the chart, the simulation and the view binding for the current
   * difficulty, discarding whatever the previous one left behind.
   *
   * The order matters. The binder is cleared *first*, while it still refers to
   * the outgoing simulation: it is the only thing that knows how many instance
   * slots were written, and blanking them afterwards would leave the previous
   * chart's last frame of waves on screen — drawn from a buffer nothing owns
   * any more, and never overwritten because the new chart starts with none.
   */
  private rebuildChart(pack: LoadedSongPack): void {
    this.binder?.clear();
    this.chart = compileSongPackChart(pack, this.currentDifficulty);
    this.simulation = new Simulation(this.chart, {
      fieldRadius: this.prototype.stage.field.radius,
      capacity: this.options.waveCapacity ?? 96,
      judge: this.judgeSettings,
      waveSpeedMultiplier: this.currentWaveSpeed,
    });
    // Measurements and score belong to the chart that produced them.
    this.spawnAccuracy.length = 0;
    this.resolutions.length = 0;
    this.judgePanel.setScore(0, 0);
    this.judgePanel.setJudgement(null);
    this.judgePanel.setCounts({}, 0, 0, 0);
    this.panel.setChart(this.currentDifficulty, this.chart.meta.eventCount, this.currentWaveSpeed);
  }

  /**
   * Opens the library and shows Song Select.
   *
   * No song is decoded here. The index is a few hundred bytes per song, so a
   * library of fifty draws instantly, and the first thing that costs anything is
   * the song the player actually picks.
   *
   * The world begins rendering immediately — a black screen while the library
   * opens would say nothing about whether anything works.
   */
  async load(): Promise<boolean> {
    this.stage = 'loading';
    void this.preloadDemo();
    this.prototype.start();
    this.select.setStatus('Opening the song library...');

    try {
      if (this.options.runtimeMode !== 'playables') await this.library.refresh();
    } catch (cause) {
      // A library that will not open is not a reason to have no game: CREATE
      // SONG and IMPORT still work, they just will not persist.
      this.select.setStatus(`The song library could not be opened: ${describeError(cause)}`, true);
      console.warn('[BridgeSession] library unavailable', cause);
    }

    if (this.library.entries.length === 0) await this.seedLibrary();

    // Started here rather than on play: the position read-out should be live
    // while Song Select is up.
    this.startPositionTicker();
    // Prepared but not shown: the title is what the player meets first, and
    // Song Select is one button away with its list already drawn.
    await this.showSelect();
    this.attachKeyboard();
    this.goToTitle();
    if (this.options.demoRequested) this.title.setDemoView(true);
    this.stage = 'ready';
    return true;
  }

  /** Which screen the player is on. */
  get flowScreen(): FlowScreen {
    return this.screen;
  }

  /** The finished run, or null when no result is being shown. */
  get playResult(): PlayResult | null {
    return this.lastResult;
  }

  /**
   * Back to the title.
   *
   * Everything the song owned is released — audio, mirrors, video textures,
   * waves — but the library is untouched, because it is not part of a run.
   */
  async goToTitle(): Promise<void> {
    this.advancedSettings.close();
    if (this.disposed) return;
    this.navigationEpoch += 1;
    this.demoLoading = false;
    this.title.setBusy(false);
    this.select.setBusy(false);
    this.result.setBusy(false);
    this.settingsPanel.setOpen(false);
    this.createScreen?.setOpen(false);
    this.discardCurrentPack();
    this.leaveDemo();
    this.lastResult = null;
    this.stage = 'ready';
    this.screen = 'title';
    this.result.hide();
    this.select.hide();
    this.backButton.hidden = true;
    this.resetJudgePanel();
    this.panel.setNote('');
    // Hidden on the title: this is a tool for a run, not part of the front door.
    this.visualPanel.setVisible(false);
    this.exportPanel.setVisible(false);
    // Same reasoning for the two run HUDs. A score of zero and an empty song
    // card on the front page look like a game that failed to load.
    this.judgePanel.setVisible(false);
    this.panel.setVisible(false);
    this.refreshTitleLibraryState();
    this.title.setDemoSelection(this.demoIndex, this.demoDifficulty);
    this.title.setDemoSpeed(this.demoSpeed);
    this.title.setDemoView(false);
    this.title.show();
  }

  async goToDemoSelect(): Promise<void> {
    await this.goToTitle();
    if (!this.disposed) this.title.setDemoView(true);
  }

  /** To Song Select, from the title or from a result. */
  async goToSelect(): Promise<void> {
    this.advancedSettings.close();
    if (this.demoLoading || this.disposed) return;
    if (this.options.runtimeMode === 'playables') {
      await this.goToTitle();
      this.title.setStatus('CUSTOM PLAY is available in NORMAL WEB. PLAY DEMO works here without uploads.');
      return;
    }
    const wasPlaying = this.currentPackId;
    this.discardCurrentPack();
    this.leaveDemo();
    this.lastResult = null;
    this.stage = 'ready';
    this.screen = 'select';
    this.title.hide();
    this.result.hide();
    this.backButton.hidden = true;
    this.resetJudgePanel();
    this.judgePanel.setVisible(false);
    this.panel.setVisible(false);
    this.select.show();
    await this.showSelect(wasPlaying ?? undefined);
  }

  /**
   * Replays the current song with the same settings.
   *
   * Reopens the pack rather than rewinding: a run is defined by a fresh
   * `Simulation`, a fresh `MusicTrack` and a clock that starts from nothing, and
   * reusing any of those would carry the previous attempt's cursor, score or
   * scheduled start into the new one. Difficulty and wave speed are read from
   * the fields the player already set, so the replay is the same chart.
   */
  async retry(): Promise<boolean> {
    if (this.demoActive) return this.playDemo();
    const packId = this.lastResult?.packId ?? this.currentPackId;
    if (packId === null || packId === undefined) return false;
    this.result.setBusy(true);
    try {
      this.result.hide();
      this.lastResult = null;
      return await this.playSong(packId);
    } finally {
      this.result.setBusy(false);
    }
  }

  /**
   * ESC, as a way back rather than a pause.
   *
   * One level per press, matching where the player is:
   *
   *   settings open -> close it        (whatever is underneath)
   *   create song open -> close it
   *   result          -> Song Select
   *   Song Select     -> Title
   *   playing         -> ignored
   *
   * Playing is deliberately inert. Half a pause — stopping input without
   * stopping `MusicClock` — is worse than none, and a real one is a phase of
   * its own.
   */
  private attachKeyboard(): void {
    if (this.onKeyDown !== null) return;
    this.onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (this.demoLoading) return;
      if (this.settingsPanel.open) {
        this.settingsPanel.setOpen(false);
        event.preventDefault();
        return;
      }
      if (this.createScreen?.open === true) {
        this.createScreen.setOpen(false);
        event.preventDefault();
        return;
      }
      if (this.screen === 'result') {
        void this.goToSelect();
        event.preventDefault();
        return;
      }
      if (this.screen === 'select') {
        void this.goToTitle();
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);
  }

  // --- Visual Art Engine ---------------------------------------------------

  /**
   * Steps the art engine, once per frame, after the world and before the frame.
   *
   * Deliberately *not* part of `stepSimulation`. Gameplay advances against
   * `MusicClock` and must not be delayed, reordered or made conditional by
   * anything visual; keeping the two hooks separate means a renderer that takes
   * a long frame costs frame rate — which is recoverable — rather than judging
   * accuracy, which is not. The engine also swallows renderer faults itself, so
   * nothing thrown below this line can reach the run.
   */
  private stepVisuals(deltaTime: number): void {
    const songTime = this.audio.clock.now();
    this.visualEngine.update(songTime, deltaTime);
    this.visualEngine.render();
  }

  /** The signal boundary, for a host that wants to drive or observe it. */
  get signals(): VisualSignalBus {
    return this.visualBus;
  }

  get visual(): VisualArtEngine {
    return this.visualEngine;
  }

  /**
   * Turns the art engine off entirely.
   *
   * This is the regression control: with it off, the frame is the one Mandalizm
   * drew before any of this existed.
   */
  setVisualEnabled(enabled: boolean): void {
    this.visualEngine.setEnabled(enabled);
  }

  /**
   * Applies an artwork recipe.
   *
   * `adoptGameplayWave` decides whether the gameplay wave settings come from
   * the recipe. They should when the recipe was *chosen* — loaded from a file,
   * or restored with a replay — because such a recipe describes a whole look.
   * They should not when the recipe was merely *generated* as a side effect of
   * switching interaction or render mode: the player's gameplay wave choice is
   * a separate setting, and silently returning it to MANDALA every time the
   * background changes is how a chosen style disappears without explanation.
   */
  applyVisualRecipe(recipe: VisualRecipe, options: { readonly adoptGameplayWave?: boolean } = {}): void {
    this.visualEngine.applyRecipe(recipe);
    if (options.adoptGameplayWave !== false) this.adoptGameplayWave(recipe);
  }

  /** Loads a recipe from JSON. Returns what was wrong, if anything. */
  loadVisualRecipe(json: string): readonly string[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch (cause) {
      return [`not valid JSON: ${describeError(cause)}`];
    }
    const result = parseRecipe(parsed);
    if (result.recipe === null) return result.problems;
    this.visualEngine.applyRecipe(result.recipe);
    this.adoptGameplayWave(result.recipe);
    return result.problems;
  }

  saveVisualRecipe(): string {
    const recipe = this.visualEngine.activeRecipe ?? defaultRecipe(randomSeed());
    // The gameplay section comes from the director rather than from the recipe
    // the engine happens to hold: the panel edits the director directly, and a
    // save that ignored that would quietly drop the user's choice.
    return serializeRecipe({ ...recipe, ...this.gameplayWaveSection() } as VisualRecipe);
  }

  updateVisualLayer(id: string, patch: Partial<VisualLayerConfig>): boolean {
    return this.visualEngine.updateLayer(id, patch);
  }

  private refreshVisualPanel(): void {
    const reports = this.visualEngine.layerReports();
    const diagnostics = this.visualEngine.diagnostics();
    this.visualPanel.setLayers(reports);
    this.visualPanel.setRenderMode(this.currentRenderMode);
    const seed = this.visualEngine.activeRecipe?.seed ?? 0;
    this.visualPanel.setSeed(seed);
    const faulted = reports.filter((entry) => entry.disabledByFault).length;
    this.refreshReplayPanel();
    this.visualPanel.setNote(
      `${reports.length} layer${reports.length === 1 ? '' : 's'}` +
        (diagnostics.droppedForCapacity > 0
          ? ` · ${diagnostics.droppedForCapacity} over the ${diagnostics.maxActiveLayers} cap`
          : '') +
        (faulted > 0 ? ` · ${faulted} disabled by faults` : ''),
    );
  }

  /**
   * Refreshes the art read-out on a timer rather than per frame.
   *
   * Seven DOM writes at 60 Hz would cost more than most of the layers do; twice
   * a second is enough to watch a value climb.
   */
  private startArtTicker(): void {
    if (this.artHandle !== 0) return;
    this.artHandle = window.setInterval(() => {
      if (!this.visualPanel.isOpen) return;
      this.visualPanel.setArtState(this.visualEngine.artState);
    }, 500);
  }

  /** Appends a layer above everything currently in the stack. */
  private addVisualLayer(renderer: string): void {
    const current = this.visualEngine.activeRecipe ?? defaultRecipe(randomSeed());
    const top = current.layers.reduce((highest, entry) => Math.max(highest, entry.zDepth), -1);
    const next: VisualRecipe = {
      ...current,
      layers: [
        ...current.layers,
        visualLayer({
          renderer,
          id: `${renderer.toLowerCase()}-${top + 1}`,
          zDepth: top + 1,
          // Additive by default: a new layer should read as arriving on top of
          // the picture rather than as replacing the part of it that was there.
          blendMode: renderer === 'MANDALA' ? 'normal' : 'add',
          opacity: renderer === 'MANDALA' ? 1 : 0.75,
          seed: current.seed + top + 1,
        }),
      ],
    };
    this.visualEngine.applyRecipe(next);
    this.refreshVisualPanel();
  }

  private removeVisualLayer(id: string): void {
    const current = this.visualEngine.activeRecipe;
    if (current === null) return;
    this.visualEngine.applyRecipe({
      ...current,
      layers: current.layers.filter((entry) => entry.id !== id),
    });
    this.refreshVisualPanel();
  }

  /**
   * Re-rolls the seed and rebuilds on the same layer stack.
   *
   * Only the seed moves. Randomising the whole recipe is `RANDOM` mode's job and
   * belongs to a later phase; this is the small, useful version — same piece,
   * different draw.
   */
  private randomizeVisualSeed(): void {
    const current = this.visualEngine.activeRecipe ?? defaultRecipe(randomSeed());
    this.visualEngine.applyRecipe({ ...current, seed: randomSeed() });
    this.refreshVisualPanel();
  }

  private downloadVisualRecipe(): void {
    const json = this.saveVisualRecipe();
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `mandalizm-recipe-${this.visualEngine.activeRecipe?.seed ?? 0}.json`;
    anchor.click();
    // Revoked on the next turn: the click is synchronous but the fetch the
    // browser makes for it is not.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private async importVisualRecipe(file: File): Promise<void> {
    try {
      const problems = this.loadVisualRecipe(await file.text());
      this.refreshVisualPanel();
      this.visualPanel.setNote(
        problems.length === 0
          ? `loaded ${file.name}`
          : `loaded ${file.name} with ${problems.length} problem(s): ${problems[0] ?? ''}`,
      );
    } catch (cause) {
      this.visualPanel.setNote(`could not read ${file.name} — ${describeError(cause)}`);
    }
  }

  get currentInteractionMode(): InteractionMode {
    return this.interactionMode;
  }

  /**
   * Chooses what a press means.
   *
   * Only the meaning changes: judging, scoring and the result are untouched, so
   * `ART` is not "SCORE with the HUD hidden" and switching modes mid-run cannot
   * corrupt a score. What differs is which signals the artwork leans on, which
   * is a decision the recipe makes downstream of this.
   */
  setInteractionMode(mode: InteractionMode | string): boolean {
    if (typeof mode === 'string' && !isInteractionMode(mode)) return false;
    this.interactionMode = mode as InteractionMode;
    // The mode's meaning lives in its mappings, so switching mode swaps the
    // recipe. Judging, scoring and the result are untouched by this line.
    const seed = this.visualEngine.activeRecipe?.seed ?? randomSeed();
    this.applyVisualRecipe(
      presetFor(this.interactionMode, {
        seed,
        available: this.visualEngine.diagnostics().available,
      }),
      { adoptGameplayWave: false },
    );
    this.visualPanel.setInteractionMode(this.interactionMode);
    this.refreshVisualPanel();
    return true;
  }

  /**
   * Chooses how the stack behaves over the length of a song.
   *
   * `FIXED` keeps whatever is loaded. `SWITCH` rotates layers on a trigger.
   * `RANDOM` generates a stack from the seed. `AUTO` listens first and then
   * generates one — so it only produces a recipe once the profiler has heard
   * enough, and holds the current one until then.
   */
  setRenderMode(mode: RenderMode): boolean {
    const current = this.visualEngine.activeRecipe;
    const seed = current?.seed ?? randomSeed();
    const available = this.visualEngine.diagnostics().available;

    switch (mode) {
      case 'RANDOM':
        this.applyVisualRecipe(
          randomRecipe({ seed, available, minLayers: 1, maxLayers: 3 }),
          { adoptGameplayWave: false },
        );
        break;
      case 'AUTO':
        this.applyVisualRecipe(
          autoRecipe({ seed, available, profile: this.profiler.profile, maxLayers: 3 }),
          { adoptGameplayWave: false },
        );
        this.autoRecipePending = !this.profiler.ready;
        break;
      case 'SWITCH': {
        const base = current ?? presetFor(this.interactionMode, { seed, available });
        this.applyVisualRecipe({ ...base, renderMode: 'SWITCH' }, { adoptGameplayWave: false });
        this.switchDirector.prepare(this.visualEngine.activeRecipe ?? base);
        this.applySwitchSelection();
        break;
      }
      default:
        if (current !== null) {
          this.applyVisualRecipe({ ...current, renderMode: 'FIXED' }, { adoptGameplayWave: false });
        }
        break;
    }
    this.refreshVisualPanel();
    return true;
  }

  get currentRenderMode(): RenderMode {
    return this.visualEngine.activeRecipe?.renderMode ?? 'FIXED';
  }

  /** Re-rolls the seed and regenerates under whatever mode is active. */
  setVisualSeed(seed: number): void {
    const current = this.visualEngine.activeRecipe;
    const mode = current?.renderMode ?? 'FIXED';
    const available = this.visualEngine.diagnostics().available;
    if (mode === 'RANDOM') {
      this.applyVisualRecipe(
        randomRecipe({ seed, available, minLayers: 1, maxLayers: 3 }),
        { adoptGameplayWave: false },
      );
    } else if (mode === 'AUTO') {
      this.applyVisualRecipe(
        autoRecipe({ seed, available, profile: this.profiler.profile, maxLayers: 3 }),
        { adoptGameplayWave: false },
      );
    } else if (current !== null) {
      this.applyVisualRecipe({ ...current, seed }, { adoptGameplayWave: false });
    }
    this.refreshVisualPanel();
  }

  private applySwitchSelection(): void {
    const recipe = this.visualEngine.activeRecipe;
    if (recipe === null) return;
    for (const patch of applySelection(recipe.layers, this.switchDirector.currentSelection())) {
      this.visualEngine.updateLayer(patch.id, { enabled: patch.enabled });
    }
  }

  // --- Replay ---------------------------------------------------------------

  /**
   * Writes the judged events into the recording.
   *
   * Reads the array the runtime already drained, so recording adds no second
   * consumer of the event queue. The running score and combo ride along with
   * each event because a replay has no simulation to ask for them.
   */
  private recordRhythmEvents(
    events: readonly SimulationEvent[],
    state: SimulationState,
  ): void {
    if (!this.recorder.isRecording) return;
    const accuracy = accuracyOf(state.score, state.totalEvents, this.judgeSettings);
    for (const event of events) {
      if (event.type !== 'waveResolved') continue;
      this.recorder.recordEvent({
        time: event.songTime,
        type: event.grade === 'miss' ? 'NOTE_MISS' : 'NOTE_HIT',
        grade: event.grade,
        offsetMs: event.timingOffsetMs,
        eventIndex: event.eventIndex,
        combo: event.combo,
        score: state.score,
        accuracy,
      });
    }
  }

  /** Starts recording the run that is about to begin. */
  private beginRecording(): void {
    const pack = this.pack;
    const chart = this.chart;
    if (pack === null || chart === null) return;
    const recipe = this.visualEngine.activeRecipe;
    this.recorder.begin(
      {
        songId: pack.manifest.songId,
        packId: pack.manifest.packId,
        displayName: pack.manifest.displayName,
        difficulty: this.currentDifficulty,
        bpm: chart.bpm,
        durationSeconds: this.track?.buffer.duration ?? 0,
        waveSpeed: this.currentWaveSpeed,
        chartHash: chartHash(chart.detectedBeatTimes, chart.events.time),
        eventCount: chart.meta.eventCount,
      },
      {
        interactionMode: this.interactionMode,
        renderMode: this.currentRenderMode,
        visualSeed: recipe?.seed ?? 0,
        originalRecipe: recipe ?? defaultRecipe(0),
      },
    );
  }

  /** Seals the recording when a run completes. */
  private finishRecording(result: PlayResult): void {
    if (!this.recorder.isRecording) return;
    this.lastReplay = this.recorder.finish(
      {
        score: result.score,
        accuracy: result.accuracy,
        hitCount: result.perfect + result.great + result.good,
        missCount: result.miss,
        maxCombo: result.maxCombo,
        totalEvents: result.totalEvents,
      },
      {
        finalArtState: this.visualEngine.artState,
        signalCounts: { ...this.signalCounts },
        mutationCount: this.visualEngine.diagnostics().mutationsDispatched,
        endTime: result.songTimeSeconds,
      },
    );
    // The panel is otherwise only told about a replay when a run *starts*, so
    // the first run of a session ended with SAVE REPLAY JSON greyed out and the
    // note reading "no replay yet — finish a run" — on the Result screen, which
    // is exactly where somebody goes to save one.
    this.refreshReplayPanel();
  }

  get replay(): ReplayData | null {
    return this.lastReplay;
  }

  get replayPlayback(): ReplayPlayer {
    return this.replayPlayer;
  }

  get isReplaying(): boolean {
    return this.replaying;
  }

  saveReplay(): string | null {
    return this.lastReplay === null ? null : serializeReplay(this.lastReplay);
  }

  /** Reads a replay from JSON. Returns what was wrong, if anything. */
  loadReplay(json: string): ReplayParseResult {
    let parsed: unknown;
    try {
      parsed = JSON.parse(json);
    } catch (cause) {
      return {
        replay: null,
        compatibility: 'incompatible',
        problems: [`not valid JSON: ${describeError(cause)}`],
      };
    }
    const result = parseReplay(parsed);
    if (result.replay !== null) this.lastReplay = result.replay;
    return result;
  }

  /**
   * Plays a recorded performance.
   *
   * The song is opened so that the world, the audio and the analyser are the
   * ones the performance happened against — the band signals are regenerated
   * from the real audio rather than stored, which is why the file stays small.
   * The simulation is *not* driven: the recorded events are the authority, so
   * a replay cannot drift from the run it is reproducing.
   *
   * `recipe` decides the interpretation. That parameter is the whole point of
   * the phase: the same timeline through a different recipe is a different
   * artwork, because no artwork was ever stored.
   */
  async playReplay(
    options: { readonly recipe?: 'original' | 'current' | 'randomize' | VisualRecipe } = {},
  ): Promise<boolean> {
    const replay = this.lastReplay;
    if (replay === null) return false;

    const entry = this.library.entries.find((item) => item.packId === replay.song.packId);
    if (entry === undefined) {
      this.visualPanel.setNote(`replay needs "${replay.song.displayName}", which is not in the library`);
      return false;
    }

    // Chosen before the song opens, because opening resets the engine.
    const chosen = this.resolveReplayRecipe(replay, options.recipe ?? 'original');

    const opened = await this.openSong(replay.song.packId);
    if (!opened) return false;
    this.title.hide();
    this.result.hide();
    this.select.hide();

    if (this.chart !== null) {
      const hash = chartHash(this.chart.detectedBeatTimes, this.chart.events.time);
      if (replay.song.chartHash.length > 0 && hash !== replay.song.chartHash) {
        this.visualPanel.setNote('warning: the chart has changed since this replay was recorded');
      }
    }

    this.setInteractionModeQuietly(replay.performance.interactionMode);
    this.applyVisualRecipe(chosen);
    this.signalCounts = {};
    this.visualEngine.reset();

    const started = await this.start();
    if (!started) return false;

    // The simulation stops being stepped: from here the recording is the truth.
    this.replaying = true;
    this.replayPlayer.load(replay);
    this.replayPlayer.play();
    this.replayComparison = null;
    this.refreshVisualPanel();
    return true;
  }

  private resolveReplayRecipe(
    replay: ReplayData,
    choice: 'original' | 'current' | 'randomize' | VisualRecipe,
  ): VisualRecipe {
    if (typeof choice === 'object') return choice;
    const available = this.visualEngine.diagnostics().available;
    switch (choice) {
      case 'current':
        return this.visualEngine.activeRecipe ?? replay.performance.originalRecipe;
      case 'randomize':
        // From the *replay's* seed, so "randomised" is still reproducible from
        // the file rather than from whenever the button was pressed.
        return randomRecipe({
          seed: replay.performance.visualSeed,
          available,
          minLayers: 1,
          maxLayers: 3,
        });
      default:
        return replay.performance.originalRecipe;
    }
  }

  /** Sets the mode without swapping the recipe, which replay chooses itself. */
  private setInteractionModeQuietly(mode: InteractionMode): void {
    this.interactionMode = mode;
    this.visualPanel.setInteractionMode(mode);
  }

  /**
   * Advances the replay, in song time.
   *
   * Driven from the simulation step so it runs once a frame against the same
   * clock the world does, and by elapsed *time* rather than by frame count — a
   * dropped frame emits everything it skipped on the next tick.
   */
  private stepReplay(): void {
    const player = this.replayPlayer;
    if (player.state !== 'playing') return;

    const songTime = this.audio.clock.now();

    // The song half of the signals keeps coming from the song, exactly as in a
    // live run: the beat grid from the chart, the bands from the audio that is
    // actually playing. Only what the *player* did comes from the file.
    this.visualSource.sampleSong(songTime);
    this.profiler.sample(this.analyser?.current ?? SILENT_LEVELS, 1 / 60);

    const delta = songTime - player.clock.currentTime;
    if (delta > 0) player.advance(delta);

    // Re-read rather than reusing the narrowed value above: `advance` is what
    // moves the player to `finished`, and the guard has already told the
    // compiler the status was `playing`.
    if (this.replayPlayer.state === 'finished') this.finishReplay();
  }

  /**
   * The recording has run out.
   *
   * The world is left running rather than snapped to a result screen: a replay
   * is being watched, and the piece keeps settling after the last event exactly
   * as it does at the end of a live run. What changes is that input is off and
   * the fidelity report is available.
   */
  private finishReplay(): void {
    const replay = this.lastReplay;
    this.replaying = false;
    this.stage = 'ready';
    if (replay !== null) {
      this.replayComparison = compareReplay(replay, {
        artState: this.visualEngine.artState,
        signalCounts: { ...this.signalCounts },
        mutationCount: this.visualEngine.diagnostics().mutationsDispatched,
      });
    }
    this.refreshVisualPanel();
  }

  /**
   * Rebuilds the artwork's behavioural inputs from a replayed signal.
   *
   * `ArtState` is fed by two different routes during a live run: mutations,
   * which arrive through the recipe, and these — the raw facts about *how* the
   * player played, which the runtime observes directly. A replay that only
   * re-emitted signals rebuilt the first and not the second, and the state
   * drifted by a third even though the game result matched exactly.
   */
  private feedArtStateFromSignal(event: VisualSignalEvent): void {
    if (event.name === 'PLAYER_INPUT') {
      const judged = event.detail.judged === true;
      const distance =
        typeof event.detail.beatDistance === 'number' ? event.detail.beatDistance : 0;
      this.visualEngine.registerInput(event.time, judged, distance);
      return;
    }
    if (event.name === 'CUSTOM_EVENT' && event.detail.kind === 'comboBreak') {
      this.visualEngine.registerComboBreak();
    }
  }

  // --- Offline re-render -----------------------------------------------------

  /**
   * Analyses the loaded song, once, and remembers the result.
   *
   * The cache key covers every input that could change the numbers, so a stale
   * timeline cannot be served: change the song, the audio, the window size or
   * the analysis code and the key changes with it.
   */
  /**
   * Band energies at one instant, for AUDIO_REACTIVE gameplay styles.
   *
   * Read from the offline analysis rather than from the live analyser, and
   * that is deliberate: the live analyser reports what is coming out of the
   * speakers *now*, which depends on when the frame happened to run. A style
   * chosen from it would differ between a run and its replay. The offline
   * timeline is a property of the song, so the same wave gets the same answer
   * every time.
   *
   * Returns null when there is no analysis, and the director falls back to its
   * default style rather than guessing.
   */
  private gameplayAudioAt(time: number): GameplayAudioSample | undefined {
    const features = this.gameplayFeatures;
    if (features === null) return undefined;
    const sample = features.sample(time);
    return {
      bass: sample.bass,
      mid: sample.mid,
      treble: sample.treble,
      // A transient anywhere in the tenth of a second around the wave's best
      // moment counts: the wave is the visual for that beat, not for one hop.
      onset: features.onsetsBetween(time - 0.05, time + 0.05) > 0 ? 1 : 0,
    };
  }

  /**
   * Reads the gameplay wave section out of a recipe, if it has one.
   *
   * Called wherever a recipe is applied, so loading a saved artwork restores the
   * gameplay styles it was saved with — and a recipe without the section puts
   * them back to MANDALA rather than leaving whatever the panel last had.
   */
  private adoptGameplayWave(recipe: VisualRecipe | null): void {
    const parsed = parseGameplayWave(recipe?.gameplayWave);
    this.setGameplayWaveSettings(parsed.settings);
  }

  /** The section as it should be written into a recipe being saved. */
  private gameplayWaveSection(): { gameplayWave?: unknown } {
    const section = serialiseGameplayWave(this.gameplayWaves.current);
    return section === undefined ? {} : { gameplayWave: section };
  }

  /** Applies gameplay wave settings, from the panel or from a recipe. */
  setGameplayWaveSettings(settings: GameplayWaveSettings): void {
    this.gameplayWaves.apply(settings);
    // Only prepared when a mode actually needs it: the analysis is the most
    // expensive thing this session can ask for, and three of the five modes
    // never look at it.
    this.gameplayFeatures =
      settings.mode === 'AUDIO_REACTIVE' ? this.audioFeatures() : null;
    this.visualPanel.setGameplayWave(settings);
  }

  get gameplayWaveSettings(): GameplayWaveSettings {
    return this.gameplayWaves.current;
  }

  /** Which style each wave has been given so far. For diagnostics and E2E. */
  gameplayWaveAssignments(): ReadonlyMap<number, GameplayWaveStyle> {
    return this.gameplayWaves.assignments();
  }

  private audioFeatures(): AudioFeatureTimeline | null {
    const track = this.track;
    const pack = this.pack;
    if (track === null || pack === null) return null;

    const samples = decodeToMono(track.buffer);
    if (samples.length === 0) return null;
    const windowSize = 2048;
    const hopSize = Math.max(1, Math.round(track.buffer.sampleRate / 100));
    const key = featureCacheKey({
      songId: pack.manifest.songId,
      audioHash: audioHash(samples, track.buffer.sampleRate),
      windowSize,
      hopSize,
    });

    const cached = this.featureCache.get(key);
    if (cached !== null) return cached;
    const analysed = analysePcm(samples, track.buffer.sampleRate, { windowSize, hopSize });
    this.featureCache.set(key, analysed);
    return analysed;
  }

  /**
   * Sets up an offline render of the loaded replay.
   *
   * The song is opened first so the chart, the beat grid and the audio are the
   * ones the performance happened against — but nothing is *played*: the render
   * takes its time from a frame counter and its band levels from the analysis,
   * so it neither needs nor tolerates a running audio clock.
   */
  async prepareReRender(
    settings: ReRenderSettings,
    options: { readonly recipe?: 'original' | 'current' | 'randomize' | VisualRecipe } = {},
  ): Promise<ReRenderProgress | null> {
    const replay = this.lastReplay;
    if (replay === null) return null;
    const entry = this.library.entries.find((item) => item.packId === replay.song.packId);
    if (entry === undefined) return null;

    const recipe = this.resolveReplayRecipe(replay, options.recipe ?? 'original');
    const opened = await this.openSong(replay.song.packId);
    if (!opened || this.chart === null) return null;

    this.title.hide();
    this.result.hide();
    this.select.hide();

    // The live loop must not touch the world while the render owns it.
    this.prototype.setFrameCallback(null);
    this.prototype.stop();

    const target = new OfflineRenderTarget(this.prototype);
    this.renderTarget = target;
    this.reRender = new ReRenderSession({
      bus: this.visualBus,
      engine: this.visualEngine,
      target,
      // A macrotask, so a cancel click can land between frames.
      yieldToHost: () =>
        new Promise((resolve) => {
          window.setTimeout(resolve, 0);
        }),
    });

    return this.reRender.prepare(
      {
        replay,
        recipe,
        beatTimes: Array.from(this.chart.detectedBeatTimes),
        bpm: this.chart.bpm,
        firstBeatOffsetSeconds: this.chart.firstBeatOffsetSeconds,
        features: this.audioFeatures(),
      },
      settings,
    );
  }



  // -------------------------------------------------------------------------
  // Live recording
  // -------------------------------------------------------------------------

  /**
   * Records what is on screen, from now.
   *
   * Deliberately not tied to the start of a run: §10 asks that a player be able
   * to decide mid-song that this one is worth keeping. The recording therefore
   * begins wherever the performance happens to be, and nothing about the run is
   * restarted or rewound to accommodate it.
   *
   * Everything that can fail is settled before the recorder starts — no
   * MediaRecorder, no canvas capture, no format this browser will write — so a
   * refusal arrives at the press rather than as silence where a file should be.
   */
  startRecording(options: { readonly automatic?: boolean } = {}): boolean {
    if (this.recording !== null) return false;
    this.releaseRecordedFile();

    const built = createLiveRecording({
      canvas: this.prototype.stage.renderer.domElement,
      config: this.recordingConfig,
      // The master bus: everything the player hears, tapped as a side branch.
      // See `CanvasAudioStream` for why this is a connect and never a reroute.
      audioSource: this.audio.master,
      audioContext: this.audio.context,
      onStatus: (status) => this.onRecordingStatus(status),
    });
    if (!built.ok) {
      this.visualPanel.setRecordingError(built.message);
      return false;
    }

    const started = built.session.start(options);
    if (!started) {
      built.session.dispose();
      return false;
    }

    this.recording = built.session;
    this.recordingAdapter = built.recorder;
    this.visualPanel.setRecordingError(null);
    this.visualPanel.setRecordingFormat(built.candidate.label);
    this.startRecordingTicker();
    return true;
  }

  /**
   * Asks the recorder to finish.
   *
   * Returns immediately; the recording is not done until the browser delivers
   * its last chunk, which arrives at `onRecordingStatus` as `DONE`. Treating
   * the request as the finish is how the tail of a song gets cut off.
   */
  stopRecording(): boolean {
    return this.recording?.stop() ?? false;
  }

  cancelRecording(): boolean {
    return this.recording?.cancel() ?? false;
  }

  setRecordingConfig(config: LiveRecordingConfig): void {
    this.recordingConfig = config;
    this.visualPanel.setRecordingConfig(config);
  }

  get recordingConfiguration(): LiveRecordingConfig {
    return this.recordingConfig;
  }

  get recordingStatus(): LiveRecordingStatus | null {
    return this.recording?.status ?? null;
  }

  /**
   * Reacts to the session's own state changes.
   *
   * The only place a recording is torn down, so every ending — finished,
   * cancelled, failed — releases the same way.
   */
  private onRecordingStatus(status: LiveRecordingStatus): void {
    this.visualPanel.setRecordingStatus(status);
    if (status.state !== 'DONE' && status.state !== 'CANCELLED' && status.state !== 'FAILED') {
      return;
    }

    this.stopRecordingTicker();
    const session = this.recording;
    const adapter = this.recordingAdapter;
    if (status.state === 'DONE' && session !== null && adapter !== null) {
      const blob = adapter.toBlob();
      const result = session.result;
      if (blob !== null && result !== null) {
        this.recordedFile = {
          blob,
          name: recordingFileName({
            song: this.lastReplay?.song.displayName ?? this.pack?.manifest.displayName ?? 'mandalizm',
            extension: result.extension,
            at: new Date(),
          }),
        };
        this.visualPanel.setRecordingResult({
          fileName: this.recordedFile.name,
          bytes: result.bytes,
          durationSeconds: result.durationSeconds,
          width: result.width,
          height: result.height,
          fps: result.fps,
          hasAudio: result.hasAudio,
          mimeType: result.mimeType,
        });
      }
    } else {
      // Cancelled or failed: no file is offered, which is the whole point of
      // cancelling. A partial capture presented as a finished one is worse than
      // nothing, because it looks fine until somebody plays it.
      this.visualPanel.setRecordingResult(null);
    }

    session?.dispose();
    this.recording = null;
    this.recordingAdapter = null;
  }

  /** Hands the finished recording to the browser. */
  private downloadRecording(): void {
    const file = this.recordedFile;
    if (file === null) return;
    const url = URL.createObjectURL(file.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.name;
    anchor.click();
    // The same ten-second margin the other exports use: revoking under a click
    // that has been dispatched but not served produces a failed download with
    // no error anywhere.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private releaseRecordedFile(): void {
    this.recordedFile = null;
    this.visualPanel.setRecordingResult(null);
  }

  /**
   * Drives the elapsed counter.
   *
   * On a timer rather than in the frame callback, so it keeps counting while
   * the render loop is doing something else and costs nothing per frame.
   */
  private startRecordingTicker(): void {
    this.stopRecordingTicker();
    this.recordingTicker = window.setInterval(() => {
      const status = this.recording?.status;
      if (status !== undefined) this.visualPanel.setRecordingStatus(status);
    }, 250);
  }

  private stopRecordingTicker(): void {
    if (this.recordingTicker !== 0) {
      window.clearInterval(this.recordingTicker);
      this.recordingTicker = 0;
    }
  }

  /**
   * Starts an automatic recording, if one is wanted and none is running.
   *
   * Called just *before* the run begins, so the recorder is already collecting
   * when the first frame and the first note arrive — §13's requirement that a
   * capture not open with half a second of nothing.
   */
  private beginAutoRecording(): void {
    if (this.demoActive || this.options.runtimeMode === 'playables') return;
    if (!this.recordingConfig.autoRecord) return;
    // A manual recording already in progress owns the session; the run does not
    // take it over, and it does not get a second one.
    if (this.recording !== null) return;
    this.startRecording({ automatic: true });
  }

  /** Stops an automatic recording when its run ends. A manual one is left alone. */
  private endAutoRecording(): void {
    const session = this.recording;
    if (session === null || !session.isAutomatic) return;
    session.stop();
  }

  // -------------------------------------------------------------------------
  // Production video export
  // -------------------------------------------------------------------------

  /**
   * Asks this machine what it can encode, once, and shows the answer.
   *
   * Done when the session starts rather than when START is pressed, so the
   * panel can grey out a codec the user cannot have instead of accepting the
   * choice and failing on it. The probes are cheap — one `isConfigSupported`
   * per candidate — and the answers do not change while the page is open.
   */
  private async reportExportCapabilities(): Promise<void> {
    const gl = this.prototype.stage.renderer.getContext();
    const maxTexture = (gl.getParameter(gl.MAX_TEXTURE_SIZE) as number) || 4096;
    const available = webCodecsAvailable();

    const video: string[] = [];
    const containers = new Set<string>();
    if (available) {
      for (const candidate of VIDEO_CODECS) {
        // 1080p is the reference question: a codec that cannot manage that is
        // not worth offering, and one that can may still be refused at 4K,
        // which is what the fallback path is for.
        const ok = await probeVideoConfig({
          codec: candidate.codec, width: 1920, height: 1080,
          framerate: 60, bitrate: 12_000_000,
        });
        if (!ok) continue;
        const name = candidate.name.replace(' baseline', '');
        if (!video.includes(name)) video.push(name);
        containers.add(candidate.container.toUpperCase());
      }
    }

    const audio: string[] = [];
    if (available && audioEncoderAvailable()) {
      for (const candidate of AUDIO_CODECS) {
        const ok = await probeAudioConfig({
          codec: candidate.codec, sampleRate: 48_000,
          numberOfChannels: 2, bitrate: 128_000,
        });
        if (ok) audio.push(candidate.name);
      }
    }

    this.exportPanel.setCapabilities({
      video, audio,
      containers: containers.size > 0 ? Array.from(containers) : ['—'],
      maxTexture, webCodecs: available,
    });
  }

  /**
   * Runs one production export, start to finished file.
   *
   * The configuration arriving here is already a snapshot taken at the button
   * press; nothing in this method reads the panel again. That matters because
   * the panel stays interactive for the minutes an export takes, and a frame
   * rate that changed halfway through would put the audio and the video on
   * different timelines.
   */
  private async startVideoExport(config: ExportConfig): Promise<void> {
    if (this.videoExport !== null) return;
    this.releaseExportedFile();

    const replay = this.lastReplay;
    if (replay === null) {
      this.exportPanel.showError(describeFailure('no-replay'));
      return;
    }

    this.exportPanel.setRunning(true);
    this.exportPanel.showProgress({
      phase: 'preparing', renderedFrames: 0, totalFrames: 0,
      elapsedSeconds: 0, realtimeRatio: 0,
    });

    const started = Date.now();
    let job: VideoExportJob | null = null;
    try {
      const prepared = await this.prepareReRender(
        {
          resolution: config.resolution, aspect: config.aspect,
          fps: config.fps, quality: config.quality, capture: false,
        },
        { recipe: config.recipe },
      );
      const session = this.reRender;
      if (prepared === null || session === null) {
        this.exportPanel.showError(describeFailure('no-replay'));
        this.exportPanel.setRunning(false);
        this.finishReRender();
        return;
      }

      const size = session.frameSizeInUse;
      const settings = {
        resolution: config.resolution, aspect: config.aspect,
        fps: config.fps, quality: config.quality, capture: true,
      };
      const built = await createAvExport({
        prototype: this.prototype,
        session,
        settings,
        width: size.width,
        height: size.height,
        audio: config.audio ? (this.track?.buffer ?? null) : null,
        ...(config.codec === 'AUTO' ? {} : { container: containerForCodec(config.codec) }),
        yieldToHost: yieldToHost,
      });

      if (!built.ok) {
        this.exportPanel.showError(
          describeFailure(built.reason as ExportFailureKind, built.message),
        );
        this.exportPanel.setRunning(false);
        this.finishReRender();
        return;
      }

      // Said out loud when AUTO could not have what it asked for. A silent
      // change of container is how somebody ends up with a WebM they cannot
      // open and no idea why.
      // The first choice in each list is what AUTO would have taken; anything
      // else means the machine refused something.
      const notice = fallbackNotice({
        wantedCodec: config.codec === 'AUTO' ? 'H.264' : config.codec,
        usedCodec: built.videoSelection.candidate.name,
        usedContainer: built.choice.container,
        size,
        wantedAudioCodec: config.audio ? 'AAC-LC' : null,
        usedAudioCodec: built.audioSelection?.candidate.name ?? null,
      });
      this.exportPanel.showNotice(notice);

      const inputs = this.reRenderInputs(replay, config.recipe);
      if (inputs === null) {
        built.session.dispose();
        this.exportPanel.showError(describeFailure('no-replay'));
        this.exportPanel.setRunning(false);
        this.finishReRender();
        return;
      }

      job = { session: built.session, cancelled: false };
      this.videoExport = job;

      const prepareResult = await built.session.prepare(inputs, built.settings, built.videoConfig);
      if (prepareResult.state === 'failed') {
        throw new Error(prepareResult.error ?? 'export could not be prepared');
      }

      const totalFrames = prepareResult.video.totalFrames;
      const outcome = await built.session.run({
        onProgress: (progress: AvExportProgress) => {
          const elapsed = (Date.now() - started) / 1000;
          this.exportPanel.showProgress({
            phase: phaseFor(progress.state),
            renderedFrames: progress.video.renderedFrames,
            totalFrames,
            elapsedSeconds: elapsed,
            realtimeRatio: replay.song.durationSeconds > 0
              ? elapsed / replay.song.durationSeconds
              : 0,
          });
        },
      });

      if (outcome.state === 'cancelled') {
        this.exportPanel.showCancelled();
      } else if (outcome.state !== 'finished' || built.session.bytes === null) {
        this.exportPanel.showError(
          describeFailure(classifyFailure(outcome.error), outcome.error ?? undefined),
        );
      } else {
        const bytes = built.session.bytes;
        const name = exportFileName({
          song: replay.song.displayName,
          container: built.choice.container,
          at: new Date(),
          ...(config.recipe === 'original' ? {} : { recipeLabel: config.recipe }),
        });
        this.exportedFile = {
          bytes,
          name,
          mime: built.choice.container === 'mp4' ? 'video/mp4' : 'video/webm',
        };
        this.exportPanel.showDone({
          fileName: name,
          container: built.choice.container,
          width: size.width,
          height: size.height,
          fps: config.fps,
          durationSeconds: replay.song.durationSeconds,
          bytes: bytes.byteLength,
          videoCodec: built.videoSelection.candidate.name,
          audioCodec: built.audioSelection?.candidate.name ?? null,
        });
      }

      built.session.dispose();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : String(cause);
      this.exportPanel.showError(describeFailure(classifyFailure(message), message));
      job?.session.dispose();
    } finally {
      this.videoExport = null;
      this.exportPanel.setRunning(false);
      // The live world comes back whatever happened, including after a failure
      // — leaving the render target installed would strand the application on a
      // stopped clock.
      this.finishReRender();
    }
  }

  /** The inputs a re-render needs, rebuilt for the export session. */
  private reRenderInputs(
    replay: ReplayData,
    choice: ExportRecipeChoice,
  ): ReRenderInputs | null {
    if (this.chart === null) return null;
    return {
      replay,
      recipe: this.resolveReplayRecipe(replay, choice),
      beatTimes: Array.from(this.chart.detectedBeatTimes),
      bpm: this.chart.bpm,
      firstBeatOffsetSeconds: this.chart.firstBeatOffsetSeconds,
      features: this.audioFeatures(),
    };
  }

  private cancelVideoExport(): void {
    const job = this.videoExport;
    if (job === null) return;
    job.cancelled = true;
    job.session.cancel();
  }

  /**
   * Hands the finished file to the browser.
   *
   * The object URL is revoked on a timer rather than immediately: a click that
   * has been dispatched has not necessarily been *served* yet, and revoking
   * under it produces a failed download with no error. Ten seconds is the same
   * margin the replay and song pack exports already use.
   */
  private downloadExportedVideo(): void {
    const file = this.exportedFile;
    if (file === null) return;
    const url = URL.createObjectURL(
      new Blob([file.bytes as unknown as BlobPart], { type: file.mime }),
    );
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.name;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private releaseExportedFile(): void {
    this.exportedFile = null;
  }

  /**
   * One frame, at full resolution, as a PNG.
   *
   * Kept separate from the video path on purpose. PNG was the Phase 5 export and
   * the reason Phase 7 exists — it managed about one frame a second — so it must
   * never be reachable from the video pipeline. As a *still* it is exactly
   * right: lossless, one frame, no timing to get wrong.
   */
  private async exportStill(): Promise<void> {
    const replay = this.lastReplay;
    if (replay === null) {
      this.exportPanel.showError(describeFailure('no-replay'));
      return;
    }
    const config = this.exportPanel.currentConfig;
    try {
      // Capture stays **off** for the run up to the chosen frame, and that is
      // the whole difference between this taking two seconds and taking three
      // minutes. With it on, `renderFrame` encodes a full-resolution PNG for
      // every frame it passes through — the Phase 5 path that measured 0.97
      // frames per second and is the reason Phase 7 exists. Only the frame
      // actually wanted is captured, once, at the end.
      const prepared = await this.prepareReRender(
        {
          resolution: config.resolution, aspect: config.aspect,
          fps: config.fps, quality: config.quality, capture: false,
        },
        { recipe: config.recipe },
      );
      const session = this.reRender;
      const target = this.renderTarget;
      if (prepared === null || session === null || target === null) {
        this.exportPanel.showError(describeFailure('no-replay'));
        return;
      }
      // Halfway through, where a piece has accumulated something worth looking
      // at but has not yet finished settling. Every frame up to that point is
      // still rendered: the artwork is cumulative, so frame N is not reachable
      // without the N-1 before it.
      const wanted = Math.floor(prepared.totalFrames / 2);
      for (let index = 0; index <= wanted; index += 1) {
        await session.renderFrame();
        if (index % 8 === 0) await yieldToHost();
        if (!session.advance()) break;
      }
      const bytes = await target.capture();
      if (bytes === null) {
        this.exportPanel.showError('The still could not be captured.');
        return;
      }
      const url = URL.createObjectURL(
        new Blob([bytes as unknown as BlobPart], { type: 'image/png' }),
      );
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = exportFileName({
        song: replay.song.displayName, container: 'mp4', at: new Date(),
      }).replace(/\.mp4$/, '.png');
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (cause) {
      this.exportPanel.showError(
        `The still could not be exported (${cause instanceof Error ? cause.message : String(cause)}).`,
      );
    } finally {
      this.finishReRender();
    }
  }

  get reRenderSession(): ReRenderSession | null {
    return this.reRender;
  }

  /**
   * Keeps the bottom-right corner readable.
   *
   * The two tool panels share a column and each one, open, is tall enough to
   * cover the other — so opening one closes the other. The open column also
   * rises over the song card, which is a display and cannot be read through a
   * panel anyway, so the card steps aside until the tools are folded away.
   */
  private onToolPanelToggled(which: 'visual' | 'export', open: boolean): void {
    if (open) {
      if (which === 'visual') this.exportPanel.setOpen(false);
      else this.visualPanel.setOpen(false);
    }
    this.syncSongCardVisibility();
  }

  /**
   * The song card shows only when there is a run and nothing covering it.
   *
   * Called on every toggle and whenever a run starts, because a panel left open
   * from the previous song would otherwise sit on top of the card the new run
   * just brought back.
   */
  private syncSongCardVisibility(): void {
    const anyOpen = this.visualPanel.isOpen || this.exportPanel.isOpen;
    const duringRun = this.screen === 'playing' || this.screen === 'result';
    this.panel.element.classList.toggle('mandalizm-song-info--demo', this.demoActive);
    this.panel.setVisible(duringRun && !anyOpen);
  }

  /** Puts the live world back after a render, whether it finished or not. */
  finishReRender(): void {
    this.reRender?.dispose();
    this.reRender = null;
    this.renderTarget?.restore();
    this.renderTarget = null;
    this.prototype.setFrameCallback(() => this.stepSimulation());
    this.prototype.start();
    this.restoreScreenOverlay();
  }

  /**
   * Brings back the screen the render hid.
   *
   * A render reopens the song and takes the world, so it hides the title,
   * select and result overlays first. Restoring the loop is only half of
   * putting things back: without this an export ends with the player looking at
   * a bare canvas — `screen` still says "result", but the card carrying RETRY,
   * SONG SELECT and TITLE is gone, and there is no way out of it.
   */
  private restoreScreenOverlay(): void {
    if (this.screen === 'result' && this.lastResult !== null) {
      this.result.setResult(this.lastResult);
      this.result.show();
      return;
    }
    if (this.screen === 'select') this.select.show();
    else if (this.screen === 'title') this.title.show();
  }

  cancelReRender(): void {
    this.reRender?.cancel();
  }

  /** The last replay's fidelity report. See `compareReplay`. */
  get replayFidelity(): ReplayComparison | null {
    return this.replayComparison;
  }

  private downloadReplay(): void {
    const json = this.saveReplay();
    if (json === null) return;
    const replay = this.lastReplay;
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    const name = (replay?.song.displayName ?? 'replay').replace(/[^\w.-]+/g, '_');
    anchor.download = `${name}.mandalizm-replay.json`;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  private async importReplay(file: File): Promise<void> {
    try {
      const result = this.loadReplay(await file.text());
      if (result.replay === null) {
        this.visualPanel.setReplayAvailable(false, `could not load — ${result.problems[0] ?? 'unknown'}`);
        return;
      }
      this.refreshReplayPanel(
        result.compatibility === 'ok'
          ? `loaded ${file.name}`
          : `loaded ${file.name} (${result.compatibility}: ${result.problems[0] ?? ''})`,
      );
    } catch (cause) {
      this.visualPanel.setReplayAvailable(false, `could not read ${file.name} — ${describeError(cause)}`);
    }
  }

  private refreshReplayPanel(note?: string): void {
    const replay = this.lastReplay;
    if (replay === null) {
      this.visualPanel.setReplayAvailable(false, note ?? 'no replay yet — finish a run');
      this.exportPanel.setSong(0, false);
      return;
    }
    // A loaded replay is exportable too, not only one that was just played.
    // Without this the panel appears solely after a live run, and somebody who
    // imported a performance from a file has no way to reach the exporter.
    this.exportPanel.setSong(this.track?.buffer.duration ?? replay.song.durationSeconds, true);
    // Present once there is something to export, and only where it can be
    // used: never on the title, never during a run. A loaded replay counts —
    // somebody who imported a performance from a file should be able to export
    // it without playing it first.
    this.exportPanel.setVisible(this.stage !== 'playing' && this.screen !== 'title');
    const inputs = replay.timeline.playerInputs.length;
    const offBeat = replay.timeline.playerInputs.filter((item) => !item.judged).length;
    this.visualPanel.setReplayAvailable(
      true,
      note ??
        `${replay.song.displayName} · ${inputs} inputs (${offBeat} off-beat) · ` +
          `${replay.timeline.rhythmEvents.length} events · seed ${replay.performance.visualSeed}`,
    );
  }

  private resetJudgePanel(): void {
    this.judgePanel.setScore(0, 0);
    this.judgePanel.setJudgement(null);
    this.judgePanel.setCounts({}, 0, 0, 0);
  }

  /**
   * Ends the run and shows the result.
   *
   * The snapshot is taken *first*, before anything is torn down, and it is
   * frozen. From this line on the render loop, the audio clock and the effect
   * bus are all still alive for a few hundred milliseconds — the result they
   * describe is already a value and none of them can touch it.
   */
  private finishRun(): void {
    const simulation = this.simulation;
    const pack = this.pack;
    if (simulation === null || pack === null || this.runComplete) return;
    this.runComplete = true;

    this.lastResult = buildPlayResult(simulation.state, {
      packId: pack.manifest.packId,
      songId: pack.manifest.songId,
      displayName: pack.manifest.displayName,
      difficulty: this.currentDifficulty,
      waveSpeed: this.currentWaveSpeed,
      durationSeconds: this.track?.buffer.duration ?? 0,
    }, this.judgeSettings);

    // Sealed with the result, from the same frozen snapshot, so the recording's
    // stored result and the one the player is shown cannot disagree.
    this.finishRecording(this.lastResult);

    // Input off before anything else: a tap landing between the snapshot and
    // the screen appearing would score into a run that is already over.
    this.stage = 'ready';
    this.screen = 'result';
    this.backButton.hidden = true;

    // The waves go, the world stays. `clear()` blanks the instance buffer, so
    // the field is empty on screen as well as in the simulation — and
    // `isRunComplete` only returns true when `activeWaves` is already zero, so
    // this is making the picture agree with a fact rather than hiding one.
    //
    // The simulation object is kept and simply stops being advanced. Discarding
    // it would leave the diagnostics with nothing to report at exactly the
    // moment someone wants to check what the run finished as.
    this.binder?.clear();
    this.track?.stop();
    this.settleMirrorsForResult();

    this.result.setResult(this.lastResult);
    this.advancedSettings.close();
    this.result.setDemo(this.demoActive);
    this.demoGuide.hide();
    // The corner HUD is a live readout of a run that has just ended, and the
    // result card behind it says the same thing properly. Two versions of the
    // same score, one of them in shorthand, is worse than one.
    this.judgePanel.setVisible(false);
    this.result.show();
    // Asked to stop here; the recording is not finished until the browser
    // delivers its last chunk, which `onRecordingStatus` waits for.
    this.endAutoRecording();
    this.exportPanel.setSong(this.track?.buffer.duration ?? 0, this.lastReplay !== null);
    this.exportPanel.setVisible(!this.demoActive && this.options.runtimeMode !== 'playables');
    this.exportPanel.clearOutcome();
  }

  /**
   * Mirror Drift during the result.
   *
   * Kept, at a quarter speed and dimmed. Cutting to a blank panel throws away
   * the one thing that makes this screen feel like the end of *this* song, and
   * the video is already decoded — stopping it buys nothing. The audio was
   * never on: Mirror Drift has been muted since Phase 1.
   */
  private settleMirrorsForResult(): void {
    for (const shard of this.prototype.mirrors.shards) {
      shard.video.element.playbackRate = 0.25;
    }
  }

  /**
   * Imports the bundled Song Pack into an empty library, once.
   *
   * Only when there is nothing at all, and only as a *seed* — it becomes an
   * ordinary library entry, indistinguishable from an imported one, and is never
   * fetched again.
   */
  private async seedLibrary(): Promise<void> {
    if (this.options.runtimeMode === 'playables') return;
    const url = this.options.songPackUrl;
    if (url === null || url === undefined || url.length === 0) return;
    try {
      this.select.setStatus('Adding the bundled Song Pack...');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status} ${response.statusText} for ${url}`);
      await this.library.addArchive(await response.arrayBuffer(), { source: 'imported' });
      this.select.setStatus('');
    } catch (cause) {
      // Genuinely optional. An empty library with a working CREATE SONG is a
      // usable game; a failed fetch of a sample is not worth a dead screen.
      this.select.setStatus(`The bundled Song Pack was not added: ${describeError(cause)}`, true);
      console.warn('[BridgeSession] seed pack unavailable', cause);
    }
  }

  /** Redraws Song Select from the library index. */
  /**
   * Re-reads the library into the title screen.
   *
   * Called wherever the library can have changed, not only when the title is
   * entered: importing or creating a song from the title screen has to leave
   * PLAY usable, and the count has to stop saying there is nothing to play.
   */
  private refreshTitleLibraryState(): void {
    const count = this.library.entries.length;
    this.title.setPlayable(true);
    this.title.setStatus(
      this.demoPreloadError ?? (this.demoLoader.prepared === null ? 'Preparing demo audio…' : count === 0
        ? this.options.demoRequested
          ? 'Demo ready — press PLAY DEMO to enable audio and begin.'
          : 'One click. Original music included. No upload needed.'
        : `${count} song${count === 1 ? '' : 's'} in the library.`),
    );
  }

  /** A different source, not a second playback or generation pipeline. */
  private async preloadDemo(): Promise<void> {
    const selection = ++this.demoSelectionVersion;
    this.title.setDemoReady(false);
    this.demoPreloadError = null;
    try {
      await Promise.all([
        this.demoLoader.preload((bytes) => this.audio.decode(bytes)),
        this.demoSfxReady ??= this.sfxPlayer.load(),
      ]);
    } catch (cause) {
      if (selection !== this.demoSelectionVersion) return;
      this.demoPreloadError = describeError(cause);
    }
    if (this.disposed || selection !== this.demoSelectionVersion) return;
    // A failed preload leaves a retry button, never an auto-play continuation.
    this.title.setDemoReady(true);
    this.refreshTitleLibraryState();
  }

  async playDemo(): Promise<boolean> {
    if (this.demoLoading || this.stage === 'playing' || this.disposed) return false;
    const navigation = this.navigationEpoch;
    if (this.demoLoader.prepared === null) {
      await this.preloadDemo();
      return false; // The next actual tap starts audio; never start after fetching.
    }
    this.demoLoading = true;
    this.title.setBusy(true);
    this.result.setBusy(true);
    this.title.setStatus('Loading bundled demo…');
    try {
      this.demoAudioError = null;
      // Keep resume AND the real source.start in the original trusted gesture.
      const resumed = this.audio.resumeFromGesture();
      if (this.disposed) return false;
      if (!this.demoActive) {
        this.customSettings = {
          difficulty: this.currentDifficulty, waveSpeed: this.currentWaveSpeed,
          recipe: this.visualEngine.activeRecipe, interactionMode: this.interactionMode,
          gameplay: this.gameplayWaves.current,
          autoRecipePending: this.autoRecipePending,
          visualEnabled: this.visualEngine.isEnabled,
        };
      }
      this.demoActive = true;
      this.judgePanel.setCompact(true);
      this.currentDifficulty = this.demoConfig.difficulty;
      this.currentWaveSpeed = this.demoConfig.waveSpeed;
      this.interactionMode = 'SCORE';
      this.autoRecipePending = false;
      this.setVisualEnabled(true);
      this.applyVisualRecipe(defaultRecipe(this.demoConfig.seed));
      if (this.demoArt) this.setInteractionMode('ART');
      this.setGameplayWaveSettings({
        mode: 'FIXED', defaultStyle: 'MANDALA', styles: {}, palette: 'NEON', seed: this.demoConfig.seed,
      });
      // The prepared Demo branch of openSong performs no awaits. The same chart
      // and playback pipeline is installed synchronously, not duplicated.
      const opening = this.openSong(this.demoConfig.packId);
      if (this.track === null || this.stage !== 'ready') throw new Error(this.error ?? 'Demo is not ready.');
      this.title.hide();
      this.result.hide();
      this.select.hide();
      const starting = this.start(resumed);
      await opening;
      const started = await starting;
      if (navigation !== this.navigationEpoch || this.disposed) return false;
      if (!started) throw new Error(this.error ?? 'Demo could not start. Please try again.');
      this.demoGuide.show();
      void this.prepareNextDemo();
      return true;
    } catch (cause) {
      if (this.disposed || navigation !== this.navigationEpoch) return false;
      const message = describeError(cause);
      this.demoAudioError = message;
      this.demoLoading = false;
      await this.goToTitle();
      this.error = message;
      this.title.setStatus(message);
      return false;
    } finally {
      if (navigation === this.navigationEpoch) {
        this.demoLoading = false;
        this.title.setBusy(false);
        this.result.setBusy(false);
      }
    }
  }

  private leaveDemo(): void {
    this.demoGuide.hide();
    this.demoActive = false;
    this.judgePanel.setCompact(false);
    this.result.setDemo(false);
    const previous = this.customSettings;
    if (previous === null) return;
    this.currentDifficulty = previous.difficulty;
    this.currentWaveSpeed = previous.waveSpeed;
    this.interactionMode = previous.interactionMode;
    this.autoRecipePending = previous.autoRecipePending;
    if (previous.recipe !== null) this.applyVisualRecipe(previous.recipe, { adoptGameplayWave: false });
    this.setGameplayWaveSettings(previous.gameplay);
    this.setVisualEnabled(previous.visualEnabled);
    this.customSettings = null;
    this.refreshVisualPanel();
  }

  private async showSelect(preferred?: string): Promise<void> {
    // Every path that adds, replaces or removes a song ends here, so this is
    // the one place that has to notice.
    this.refreshTitleLibraryState();
    this.select.setSongs(this.library.entries, preferred ?? this.currentPackId ?? null);
    this.select.setWaveSpeeds(WAVE_SPEED_PRESETS, this.currentWaveSpeed);
    const packId = this.select.selected;
    if (packId !== null) await this.previewSong(packId);
    await this.reportStorage();
    if (this.options.runtimeMode === 'playables') {
      this.select.setStatus('Custom songs are available in NORMAL WEB. Press Escape to return to Demo.');
    }
  }

  /**
   * Shows a song's details without opening it.
   *
   * Reads the index record, plus the cover — one image, for one song, because it
   * is on screen. Nothing else is touched: the music, the charts and the video
   * stay in storage until PLAY.
   */
  private async previewSong(packId: string): Promise<void> {
    const entry = this.library.find(packId);
    if (entry === null) return;
    this.select.setSelected(packId);
    this.select.setDetail(entry);

    const choices = difficultyChoices(entry);
    if (!choices.some((choice) => choice.value === this.currentDifficulty)) {
      this.currentDifficulty = (choices[0]?.value as Difficulty | undefined) ?? 'normal';
    }
    this.select.setDifficulties(choices, this.currentDifficulty);
    await this.showCoverFor(entry);
  }

  private async showCoverFor(entry: SongEntry): Promise<void> {
    this.cover?.close();
    this.cover = null;
    this.select.setCover(null);
    if (!entry.hasCover) return;
    try {
      const blob = await this.library.openCover(entry.packId);
      if (blob === null) return;
      // Guard against a fast click-through: the selection may have moved on
      // while this decoded.
      if (this.select.selected !== entry.packId) return;
      this.cover = await createImageBitmap(blob);
      this.select.setCover(this.cover);
      this.panel.setCover(this.cover);
    } catch (cause) {
      // The song is perfectly playable without its artwork.
      console.warn('[BridgeSession] cover could not be decoded', cause);
    }
  }

  /**
   * Opens a song and starts it.
   *
   * One button, one gesture: PLAY reads the song out of storage, decodes it,
   * resolves its music video, and starts. Splitting that into "load" and "start"
   * would put a second click between the player and the song for no reason —
   * the gesture that must exist for audio is this one.
   */
  async playSong(packId: string): Promise<boolean> {
    if (this.demoActive && packId !== this.demoConfig.packId) return false;
    if (this.disposed || (this.options.runtimeMode === 'playables' && !this.demoActive)) return false;
    if (this.stage === 'playing') return false;
    const navigation = this.navigationEpoch;
    this.select.setBusy(true);
    try {
      // Normal/custom playback also unlocks output before IndexedDB or decoding.
      const resumed = this.demoActive || await this.audio.resumeFromGesture();
      if (this.disposed || navigation !== this.navigationEpoch) return false;
      if (!resumed) {
        this.select.setStatus('Audio could not start. Please press PLAY again.', true);
        return false;
      }
      const opened = await this.openSong(packId);
      if (!opened || navigation !== this.navigationEpoch) return false;
      this.title.hide();
      this.result.hide();
      this.select.hide();
      // `start` sets both `stage` and `screen`, together, at the instant the run
      // becomes live. Setting `screen` again here would be a second opinion.
      return await this.start();
    } finally {
      this.select.setBusy(false);
    }
  }

  /**
   * Reads one song out of the library and makes the runtime ready to play it.
   *
   * Re-enterable by construction: everything the previous song owned — decoded
   * audio, cover bitmap, instance buffers, video decoders, the object URL its MV
   * was reached through — is released by `discardCurrentPack` before any of this
   * song's is created.
   */
  private async openSong(packId: string): Promise<boolean> {
    this.discardCurrentPack();
    const epoch = this.packEpoch;
    const cancelled = () => this.disposed || epoch !== this.packEpoch;
    this.stage = 'loading';
    try {
      this.select.setStatus('Reading the song...');
      const { entry, pack, video } = this.demoActive && packId === this.demoConfig.packId
        ? this.demoLoader.prepared!.song
        : await this.library.open(packId);
      if (cancelled()) return false;
      this.pack = pack;
      this.currentPackId = packId;
      this.packSource = this.demoActive ? `demo:${this.demoConfig.id}:v${this.demoConfig.version}` : `library:${packId}`;

      // Created before the chart, and kept across difficulty switches: it holds
      // the record of which instance slots are dirty, which is exactly the
      // state a swap has to clear rather than abandon.
      this.binder = new WaveViewBinder(this.prototype.ripples);
      // The seam from Phase 8A. MANDALA keeps going down the old path; the
      // director decides which waves take the other one.
      this.binder.setGameplayVisual(
        this.prototype.gameplayWaves,
        (wave) => this.gameplayWaves.styleFor(wave.eventIndex, this.gameplayAudioAt(wave.bestTime)),
        // Separate stream, so changing the material mode leaves the colours
        // alone and re-rolling the palette leaves the materials alone.
        (wave) => this.gameplayWaves.pigmentFor(wave.eventIndex),
      );
      this.gameplayWaves.reset();
      if (this.options.previewNoteWords !== false) {
        // Derived from the event index so the same event always highlights the
        // same word, matching how the seal's own appearance is decided.
        this.binder.setNoteWordSelector((wave, words) => wave.eventIndex % words);
      }
      // The simulation reads the compiled chart and nothing else; it has no
      // idea where the archive came from.
      this.rebuildChart(pack);
      // The beat grid the art engine will run on: the one the chart engine
      // detected in this audio, not one synthesised from BPM.
      this.visualSource.setChart(this.chart);
      this.profiler.setBpm(this.chart?.bpm ?? 0);
      if (this.currentRenderMode === 'SWITCH') {
        const recipe = this.visualEngine.activeRecipe;
        if (recipe !== null) this.switchDirector.prepare(recipe);
      }
      // When the music may stop mattering. The grace period is measured from
      // the last chart event rather than from the audio's length, because a
      // pack can carry minutes of silence after its final note.
      const times = this.chart?.events.time;
      this.lastEventTime = times === undefined || times.length === 0
        ? 0
        : (times[times.length - 1] ?? 0);
      this.runComplete = false;

      // Started here and awaited below: decoding a 57 KB effect alongside a
      // 20 MB song costs nothing, and it cannot fail the load either way.
      // Demo uses the existing synthesized click; no optional-file 404s.
      const sfxReady = this.demoActive
        ? Promise.resolve() : this.sfxPlayer.load();

      this.select.setStatus('Decoding audio...');
      // `copyMusicArrayBuffer` hands out a fresh copy precisely because
      // `decodeAudioData` detaches what it is given.
      const buffer = this.demoActive ? this.demoLoader.prepared!.buffer
        : await this.audio.decode(copyMusicArrayBuffer(pack));
      if (cancelled()) return false;
      this.track = new MusicTrack(this.audio, buffer);
      // Restore audio-reactive CUSTOM visuals against this song, never the demo.
      if (!this.demoActive) this.setGameplayWaveSettings(this.gameplayWaves.current);
      if (!this.demoActive) await sfxReady;
      if (cancelled()) return false;

      this.panel.setSong({
        displayName: pack.manifest.displayName,
        author: pack.manifest.author,
        bpm: pack.manifest.bpm,
        difficulty: this.difficulty,
        eventCount: this.chart?.meta.eventCount ?? 0,
        waveSpeed: this.currentWaveSpeed,
        durationSeconds: buffer.duration,
        packId: pack.manifest.packId,
      });

      // Now the real song length is known, install the per-frame step. The
      // hook runs before anything is drawn, so the frame renders the state the
      // clock implies rather than the previous frame's.
      this.prototype.setFrameCallback(() => this.stepSimulation());

      if (!this.demoActive) await this.loadCover(pack);
      if (cancelled()) return false;

      this.select.setStatus('Resolving music video...');
      // The Blob never becomes bytes: `createObjectURL` hands the video element
      // a handle to the browser's own copy, so a 200 MB MV costs a string here.
      if (video !== null) {
        this.packVideoUrl = URL.createObjectURL(
          video.type.length > 0
            ? video
            : new Blob([video], { type: videoMimeFor(entry.mvReference ?? 'video.mp4') }),
        );
        this.createdVideoUrls += 1;
      }
      if (!this.demoActive && this.options.runtimeMode !== 'playables') await this.prototype.mirrors.load({
        displayName: pack.manifest.displayName,
        extensions: pack.manifest.extensions,
        ...(this.packVideoUrl === null ? {} : { packVideoUrl: this.packVideoUrl }),
      });
      if (cancelled()) return false;
      this.reportMirror();

      this.stage = 'ready';
      this.error = null;
      this.select.setStatus('');
      return true;
    } catch (cause) {
      if (cancelled()) return false;
      this.stage = 'failed';
      this.error = describeError(cause);
      this.select.setStatus(this.error, true);
      this.panel.setNote('Load failed.');
      if (!this.demoActive) console.error('[BridgeSession] song could not be opened', cause);
      // Back to a usable state: the library is intact, and another song may
      // well work.
      this.discardCurrentPack();
      this.stage = 'ready';
      return false;
    }
  }

  /**
   * Stops the song and returns to Song Select.
   *
   * A full teardown, not a pause. Everything the song owned is released here,
   * so picking the next one starts from the same state the first one did.
   */
  async returnToSelect(): Promise<void> {
    if (this.demoActive) return this.goToSelect();
    // Read before the teardown clears it: the song just played should still be
    // the highlighted one, so leaving and coming back does not move the cursor.
    const wasPlaying = this.currentPackId;
    this.discardCurrentPack();
    this.stage = 'ready';
    this.judgePanel.setScore(0, 0);
    this.judgePanel.setJudgement(null);
    this.judgePanel.setCounts({}, 0, 0, 0);
    this.select.show();
    this.backButton.hidden = true;
    await this.showSelect(wasPlaying ?? undefined);
  }

  private async reportStorage(): Promise<void> {
    try {
      this.select.setStorage(await this.library.storage());
    } catch {
      // A missing estimate is not worth a message.
    }
  }

  /**
   * Releases everything tied to the current song, before another takes its place.
   *
   * The order at the end is load-bearing. Mirror Drift is unloaded **first**, so
   * every `<video>` has dropped the object URL, and only then is the URL
   * revoked. Revoking a URL an element still holds open leaves the browser with
   * a handle to something it has been told no longer exists — which is how a
   * "why is the previous song's video still on screen" bug is built.
   */
  private discardCurrentPack(): void {
    this.audioLifecycleEpoch += 1;
    this.packEpoch += 1;
    this.sfxPlayer.stopAll();
    this.gameplayStatus.hide();
    this.schedulerStarted = false;
    this.simulationUpdates = 0;
    this.binder?.clear();
    this.binder = null;
    this.prototype.setFrameCallback(null);
    this.simulation = null;
    this.chart = null;
    this.track?.dispose();
    this.track = null;
    // The artwork's inputs go with the song too: envelopes, beat index and the
    // analyser's transient history all describe a run that is over. Layers keep
    // their seeds, so the same recipe on the next song is still reproducible.
    this.visualSource.setChart(null);
    this.visualSource.reset();
    this.visualEngine.reset();
    this.profiler.reset();
    this.lastCombo = 0;
    // A replay belongs to the song it was recorded against. The *file* survives
    // — that is the point of saving it — but the playback in progress does not.
    this.replaying = false;
    this.replayPlayer.stop();
    if (this.recorder.isRecording) this.recorder.cancel();

    // Musical time belongs to the song, not to the session.
    //
    // Without this the clock keeps reporting the *previous* song's elapsed time
    // right up until the next `startAt`, and `openSong` installs the frame
    // callback before `start()` runs — so every frame drawn while the new song
    // decodes its audio and resolves its video advances the fresh simulation to
    // a time near the end of the old one. The chart is consumed and missed
    // wholesale before the music has played a note: RETRY, and any second song,
    // ends instantly with a full complement of misses.
    //
    // Reset leaves the clock idle, and an idle clock reads exactly zero, which
    // is the same thing the very first song of a session sees.
    this.audio.clock.reset();
    this.cover?.close();
    this.cover = null;
    this.pack = null;
    this.currentPackId = null;
    this.startSkewMs = null;
    this.runComplete = false;
    this.lastEventTime = 0;
    this.spawnAccuracy.length = 0;
    this.resolutions.length = 0;

    this.prototype.mirrors.unload();
    if (this.packVideoUrl !== null) {
      URL.revokeObjectURL(this.packVideoUrl);
      this.revokedVideoUrls += 1;
      this.packVideoUrl = null;
    }
  }

  /**
   * CREATE SONG, end to end.
   *
   * ```
   * chosen audio -> SongCreationService -> Python chart engine
   *              -> SongPack v1 bytes   -> this session       -> START
   * ```
   *
   * The generated pack goes straight into the library and becomes the selected
   * song. There is no exported ZIP to re-import by hand, and nothing about the
   * resulting entry differs from one that came from a pack Unity made.
   *
   * The music video, if one was chosen, never reaches the chart engine — Python
   * analyses audio, and sending it 200 MB of video would be 200 MB of upload for
   * a file it would ignore. It is attached to the pack afterwards.
   */
  private async generateSong(fields: CreateSongFields, regenerateSection?: number): Promise<void> {
    const service = this.creation;
    const screen = this.createScreen;
    if (service === null || screen === null) return;
    // The service refuses a second run itself; this is so the screen can say why
    // rather than showing a rejection that looks like an engine failure.
    if (service.busy) return;
    if (this.stage === 'playing') {
      screen.addError('The song is already playing. Go back to Song Select to create another.');
      return;
    }
    if (fields.audio === null) {
      screen.addError('Select an audio file first.');
      return;
    }

    screen.resetStatus();
    screen.setBusy(true);
    let created: CreatedSong;
    try {
      created = await service.create(
        {
          audio: fields.audio,
          title: fields.title,
          artist: fields.artist,
          cover: fields.cover ?? undefined,
          // AUTO sends nothing, so the engine decides everything exactly as
          // it always has. CUSTOM sends positions, never meanings.
          ...(fields.mode === 'CUSTOM' ? { mode: fields.mode, tuning: fields.tuning } : {}),
          // Regenerating one section is always a *directed* request, never an
          // automatic one: the direction being sent is the one on screen —
          // whatever the engine wrote, plus whatever the player then changed —
          // and asking the engine to redecide would throw that edit away.
          ...(regenerateSection === undefined && fields.mode === 'DIRECTED_AUTO'
            ? { autoDirect: true, ...(fields.semantic ? {} : { semantic: false }) }
            : {}),
          ...(regenerateSection === undefined || fields.lastDirection === null
            ? {}
            : {
                mode: 'CUSTOM' as const,
                direction: fields.direction,
                baselineDirection: fields.lastDirection,
                // What the pack actually is, handed back untouched: the
                // charts so the engine can copy every note outside the
                // section being redone, and the direction it reported for
                // each difficulty so the ones it does regenerate start from
                // what made them. Neither is rebuilt here — a baseline this
                // screen reconstructed would be a baseline that can drift.
                ...(this.baselineCharts === null ? {} : { baselineCharts: this.baselineCharts }),
                ...(this.resolvedDirections === null
                  ? {}
                  : { resolvedDirections: this.resolvedDirections }),
                regenerateSection,
              }),
          // Sent whatever the mode says: a direction is a request in its own
          // right, and the client drops it when it asks for nothing.
          direction: fields.direction,
        },
        {
          onPhase: (phase) => screen.setPhase(phase),
          onProgress: (progress) =>
            screen.setStage(progress.stage, progress.message, progress.fraction),
          onNote: (note) => screen.addNote(note),
        },
      );
    } catch (cause) {
      screen.setBusy(false);
      const message = isChartGenerationError(cause)
        ? `${cause.code}: ${cause.message}`
        : cause instanceof Error
          ? cause.message
          : String(cause);
      // Reported on the screen and nowhere else. A failed generation must leave
      // the loaded song, the audio graph and the render loop exactly as they
      // were — the player can close this and play what they already had.
      screen.addError(message);
      console.warn('[BridgeSession] song creation failed', cause);
      return;
    }

    const { summary, eventCounts } = created;
    // Shown for AUTO too: the point of the panel is that an automatic chart
    // stops being a black box, so the reasoning appears whether or not the
    // player asked to correct anything.
    screen.setSongDuration(summary.durationSeconds);
    // The measured sections, what the engine decided for each, and — when it
    // read the song's structure — what it inferred each one to be. The screen
    // renders the three separately; blurring them is the one thing this panel
    // must never do.
    screen.showDetectedSections(
      summary.sectionAnalysis,
      summary.sectionDirection,
      summary.semanticAnalysis,
    );
    // The engine may have written the direction itself; if it did, that is
    // the one the pack was built with and the one a section regeneration has
    // to hold everything else still against.
    const applied = directionFromWire(summary.appliedDirection);
    if (applied !== null && summary.autoDirected) screen.adoptDirection(applied);
    screen.setLastDirection(applied ?? (fields.mode === 'AUTO' ? null : fields.direction));
    if (summary.semanticDirected && summary.semanticAnalysis !== null) {
      const readings = summary.semanticAnalysis.sections;
      const named = readings.filter((item) => item.semanticRole !== 'UNKNOWN');
      const groups = new Set(
        readings.filter((item) => item.similarToSections.length > 0).map((item) => item.recurrenceGroup),
      );
      screen.addNote(
        `structure — ${named.length} of ${readings.length} sections named, ` +
          `${groups.size} recurring: ` +
          (named
            .slice(0, 6)
            .map((item) => `${item.semanticRole}? ${item.semanticRoleConfidence.toFixed(2)}`)
            .join(', ') || 'none confidently'),
      );
    }
    if (summary.sectionAnalysis !== null) {
      const sections = summary.sectionAnalysis.sections;
      screen.addNote(
        `sections — ${sections.length} found: ` +
          sections.slice(0, 8).map((section) => `${section.label} ${section.startTime.toFixed(0)}s`).join(', '),
      );
    }
    if (summary.regeneratedSection !== null) {
      screen.addNote(
        `section ${summary.regeneratedSection} regenerated; every note outside it was copied ` +
          'from the chart you already had.',
      );
    }
    // Held for the next REGENERATE SECTION. Read out of the archive rather
    // than out of the loaded pack: what goes back to the engine has to be the
    // JSON the pack stores, not the runtime's reading of it.
    this.resolvedDirections =
      Object.keys(summary.resolvedDirections).length === 0 ? null : summary.resolvedDirections;
    try {
      this.baselineCharts = readRawChartDocuments(created.archive);
    } catch (cause) {
      // Not fatal: without them the engine falls back to regenerating the
      // baseline from its own direction, which is what it did before.
      this.baselineCharts = null;
      console.warn('[BridgeSession] the created pack could not be re-read for regeneration', cause);
    }
    // A section's breakdown is not the song's, so it does not go in the panel
    // that reports what the analysis measured.
    screen.showAnalysis(
      summary.autoParameters,
      summary.eventSourcesScope === 'section' ? null : summary.eventSources,
    );
    screen.showFeedback(true);
    const shapes = summary.geometrySequence?.['normal'];
    if (shapes !== undefined && shapes.length > 0) {
      // The sequence, not just the tally: a mix that never mixed and one that
      // did look identical in a count of chunks per shape.
      screen.addNote(`geometry per chunk — ${shapes.slice(0, 12).join(' → ')}`);
    }
    const normalSources = summary.eventSources?.['normal'];
    if (normalSources !== undefined) {
      // After a regeneration the engine can only count the section it made;
      // saying so is the difference between a number and a wrong number.
      const scope =
        summary.eventSourcesScope === 'section'
          ? `section ${summary.regeneratedSection ?? ''} note sources`.trim()
          : 'normal note sources';
      screen.addNote(
        `${scope} — ${normalSources['beat'] ?? 0} beat / ` +
          `${normalSources['melody'] ?? 0} melody / ${normalSources['ornament'] ?? 0} onset ` +
          `(${summary.generatorMode}, engine ${summary.generatorVersion})`,
      );
    }
    screen.addResult(
      `BPM ${summary.detectedBpm.toFixed(1)} · ${summary.durationSeconds.toFixed(1)}s · ` +
        `easy ${eventCounts.easy} / normal ${eventCounts.normal} / hard ${eventCounts.hard} · ` +
        `${(summary.archiveBytes / (1024 * 1024)).toFixed(1)} MiB in ` +
        `${summary.elapsedSeconds.toFixed(1)}s`,
    );

    try {
      if (fields.video !== null) {
        screen.addNote(
          `Bundling ${fields.video.name} (${formatBytes(fields.video.size)}) with the song. ` +
            'It was not sent to the chart engine; only the audio was.',
        );
      }
      screen.addResult('Saving to the library...');
      const outcome = await this.library.addArchive(created.archive, {
        source: 'created',
        durationSeconds: summary.durationSeconds,
        ...(fields.video === null
          ? {}
          : { video: { blob: fields.video, fileName: fields.video.name } }),
      });
      if (outcome.status === 'duplicate') {
        // Essentially impossible — ids are minted from the clock — but silently
        // discarding a song someone waited for would be worse than saying so.
        screen.addError(`A song with the id ${outcome.existing.packId} is already stored.`);
        return;
      }
      // Selected, not loaded: nothing has been decoded, and `currentPackId`
      // means "the song whose assets this session is holding".
      await this.showSelect(outcome.entry.packId);
      screen.addResult(`Saved. "${summary.displayName}" is selected — press PLAY.`);
      screen.setOpen(false);
    } catch (cause) {
      screen.addError(`The song was generated but could not be stored: ${describeError(cause)}`);
      console.error('[BridgeSession] created song could not be stored', cause);
    } finally {
      screen.setBusy(false);
    }
  }

  /**
   * IMPORT SONGPACK.
   *
   * The file goes through the same reader everything else does, so a pack that
   * would not play is refused here with a reason rather than accepted and
   * discovered later. A collision on `packId` stops and asks; nothing is
   * overwritten without an answer.
   */
  private async importSongPack(file: File): Promise<void> {
    if (this.options.runtimeMode === 'playables') return;
    this.select.setBusy(true);
    try {
      this.select.setStatus(`Reading ${file.name}...`);
      const bytes = new Uint8Array(await file.arrayBuffer());
      let outcome = await this.library.addArchive(bytes, { source: 'imported' });

      if (outcome.status === 'duplicate') {
        const existing = outcome.existing;
        const replace = await this.select.ask(
          'Song already exists',
          `"${existing.displayName}" is already in the library with the same pack id ` +
            `(${existing.packId}).\n\nReplacing it overwrites its audio, charts, cover and video.`,
          'REPLACE',
          true,
        );
        if (!replace) {
          this.select.setStatus(`${file.name} was not imported; the song is already here.`);
          return;
        }
        if (existing.packId === this.currentPackId && this.stage === 'playing') {
          this.select.setStatus('That song is playing. Go back to Song Select first.', true);
          return;
        }
        outcome = await this.library.addArchive(bytes, { source: 'imported', replace: true });
      }

      if (outcome.status === 'duplicate') return;
      await this.showSelect(outcome.entry.packId);
      this.select.setStatus(
        `${outcome.status === 'replaced' ? 'Replaced' : 'Imported'} "${outcome.entry.displayName}"` +
          `${outcome.entry.hasVideo ? ' with its music video' : ''}.`,
      );
    } catch (cause) {
      // The library is untouched: nothing is written until the whole archive has
      // been read and validated.
      this.select.setStatus(`${file.name} could not be imported — ${describeError(cause)}`, true);
      console.warn('[BridgeSession] import failed', cause);
    } finally {
      this.select.setBusy(false);
    }
  }

  /** EXPORT SONGPACK: rebuilds a `.zip` from stored assets and offers it. */
  private async exportSongPack(packId: string): Promise<void> {
    if (this.options.runtimeMode === 'playables') return;
    this.select.setBusy(true);
    try {
      const entry = this.library.find(packId);
      this.select.setStatus(`Writing ${entry?.displayName ?? packId}...`);
      const { blob, fileName, report } = await this.library.exportArchive(packId);
      // Named after the song rather than the pack id, since this file is going
      // to sit in somebody's downloads folder.
      const download = document.createElement('a');
      const url = URL.createObjectURL(blob);
      download.href = url;
      download.download = safeFileName(entry?.displayName ?? packId, fileName);
      download.click();
      // Revoked on the next turn: revoking synchronously can cancel the download
      // before the browser has taken its own reference.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      this.select.setStatus(
        `Exported ${download.download} — ${formatBytes(blob.size)} in ${report.elapsedMs} ms` +
          `${entry?.hasVideo === true ? ', music video included' : ''}.`,
      );
    } catch (cause) {
      this.select.setStatus(`Export failed — ${describeError(cause)}`, true);
      console.error('[BridgeSession] export failed', cause);
    } finally {
      this.select.setBusy(false);
    }
  }

  /** DELETE SONG: index record, audio, charts, cover and video, after asking. */
  private async deleteSong(packId: string): Promise<void> {
    if (this.options.runtimeMode === 'playables') return;
    const entry = this.library.find(packId);
    if (entry === null) return;
    if (packId === this.currentPackId && this.stage === 'playing') {
      this.select.setStatus('That song is playing and cannot be deleted.', true);
      return;
    }
    const confirmed = await this.select.ask(
      `Delete "${entry.displayName}"?`,
      'This removes the local Song Pack, audio, charts, cover and video from this browser.\n' +
        'Exported copies are not affected.',
      'DELETE',
      true,
    );
    if (!confirmed) return;

    this.select.setBusy(true);
    try {
      // The song may be *open* without playing — selected, decoded, ready. Let
      // go of it before its bytes disappear underneath.
      if (packId === this.currentPackId) {
        this.discardCurrentPack();
        this.currentPackId = null;
      }
      await this.library.remove(packId);
      await this.showSelect();
      this.select.setStatus(`Deleted "${entry.displayName}".`);
    } catch (cause) {
      this.select.setStatus(`Delete failed — ${describeError(cause)}`, true);
      console.error('[BridgeSession] delete failed', cause);
    } finally {
      this.select.setBusy(false);
    }
  }

  /**
   * Starts the song and the mirrors.
   *
   * Must be reached from a user gesture. Both are started here, and the audio's
   * scheduled context timestamp is what anchors `MusicClock`, so the clock's
   * origin is the audio's origin rather than an approximation of it.
   *
   * Nothing about becoming playable waits on the music video. The rule is that
   * the clock, the input gate and the gameplay HUD all become live together, in
   * the same turn the clock is anchored; the mirrors settle whenever they do.
   */
  async start(gestureResume?: Promise<boolean>): Promise<boolean> {
    this.advancedSettings.close();
    if (this.stage !== 'ready' || this.track === null) return false;
    const epoch = this.packEpoch;
    const startingTrack = this.track;

    if (this.pageHidden || document.hidden) {
      this.error = 'The page was hidden before playback began. Return and press PLAY again.';
      this.select.setStatus(this.error, true);
      this.select.show();
      return false;
    }

    const lead = Math.max(0.05, this.pack?.manifest.dspLeadTime ?? 0.2);
    // In Demo this invokes source creation/connect/start before the first await.
    const gestureStart = gestureResume === undefined ? null
      : this.track.scheduleStart(this.audio.context.currentTime + lead);
    const resumed = await (gestureResume ?? this.audio.resume());
    if (this.disposed || epoch !== this.packEpoch || this.track !== startingTrack) return false;
    if (!resumed || this.pageHidden || document.hidden) {
      if (gestureStart !== null) this.track.stop();
      await this.audio.suspend();
      this.error = 'Audio could not start while the page was inactive. Return and press PLAY again.';
      this.select.setStatus(this.error, true);
      this.select.show();
      return false;
    }

    // Issued first and never awaited. `mirrors.play()` starts every `video.play()`
    // synchronously and only then settles its own bookkeeping, so letting that
    // settling land between the two starts is what an earlier version measured
    // as a 150 ms skew that had nothing to do with the media.
    // Built on the first start, not in the constructor: an `AudioContext` that
    // has never been resumed has no meaningful sample rate to size the bands
    // against. It taps the music gain as a side branch and is never in the path
    // to the speakers, so nothing here can change what the player hears.
    if (this.analyser === null) {
      this.analyser = new MusicAnalyser({ context: this.audio.context, source: this.audio.music });
      this.visualSource.setAnalyser(this.analyser);
    }

    const mirrorAt = performance.now();
    const mirrorStarted = this.prototype.mirrors.play();

    const when = gestureStart ?? this.track.scheduleStart(this.audio.context.currentTime + lead);
    this.audio.clock.startAt(when);
    this.schedulerStarted = true;
    this.lastAudioProgress = this.audio.context.currentTime;
    this.audioProgressAt = performance.now();
    this.startSkewMs = performance.now() - mirrorAt;

    // The run is live the instant the clock has an origin, and everything that
    // makes it playable goes live with it — in this turn, not a later one.
    //
    // This used to sit behind `await mirrorStarted`, which made the music video
    // a precondition for playing the game. `HTMLMediaElement.play()` does not
    // resolve when playback is *requested*, it resolves when playback has
    // actually begun, so six decoders opening a large MV can hold that promise
    // for tens of seconds. Everything below was waiting on it: `stage` gates the
    // `pointerdown` handler, so taps were dropped; the Song Select button stayed
    // hidden; and `playSong` only sets `flow` once this resolves. The player saw
    // a song that played, drew and scrolled past its notes while refusing every
    // press — and then, at the moment the MV finally started, the HUD appeared
    // and judging began, together, because they were the same await.
    //
    // The music video is scenery. It may take as long as it likes.
    // `stage` and `screen` are set together, on one line of execution, because
    // they answer the same question and anything that reads one while the other
    // still says otherwise is reading a run that half exists. `stage` gates
    // input; `screen` routes ESC and the result. Setting `screen` from the
    // caller instead left it a turn behind, which is a smaller version of the
    // very bug this method just stopped having.
    this.stage = 'playing';
    this.screen = 'playing';
    this.prototype.start();
    // Recording starts with the run, not with the song loading, so the timeline
    // shares its origin with `MusicClock`. Skipped while replaying: a replay is
    // a performance being read, not a new one being given.
    this.signalCounts = {};
    // Before the run, not after: §13 asks that the opening frame and the first
    // note be in the file, and a recorder started afterwards begins with
    // whatever fraction of a second it took to build.
    this.beginAutoRecording();
    if (!this.replaying) this.beginRecording();
    this.select.hide();
    // The way back. Without it, a session plays one song and then needs a page
    // reload, which is the whole thing this phase exists to stop.
    this.backButton.hidden = false;
    this.judgePanel.setVisible(true);
    this.visualPanel.setVisible(!this.demoActive && this.options.runtimeMode !== 'playables');
    this.syncSongCardVisibility();
    // Not during play. Export acts on a finished performance, and the brief has
    // been explicit since Phase 3 that the play surface stays clear.
    this.exportPanel.setVisible(false);
    this.refreshVisualPanel();
    this.startPositionTicker();
    this.reportMirror();

    // Reported when it settles, so the diagnostics still show how the MV ended
    // up. A failure here costs the scenery and nothing else.
    void mirrorStarted.then(
      () => this.reportMirror(),
      (cause: unknown) => {
        console.warn('[BridgeSession] mirror playback did not start', cause);
        this.reportMirror();
      },
    );
    return true;
  }

  /**
   * Advances the world to the current musical time.
   *
   * `MusicClock.now()` is the only input. It returns a negative value before the
   * scheduled start, which `advanceTo` clamps, so the pre-roll draws an empty
   * field rather than firing the opening events early.
   */
  private stepSimulation(): void {
    // A replay drives the visuals from its own timeline. Stepping the simulation
    // as well would judge the song a second time, and the two verdicts would
    // race — the recording must be the only authority while it plays.
    if (this.replaying) {
      this.stepReplay();
      return;
    }

    // Loading, interrupted audio and background pages must not consume notes.
    if (this.stage !== 'playing' || !this.schedulerStarted || !this.audio.running ||
        this.pageHidden || document.hidden) return;
    this.simulationUpdates += 1;

    const simulation = this.simulation;
    const binder = this.binder;
    if (simulation === null || binder === null) return;
    // Once the run is over the clock keeps advancing and the loop keeps
    // drawing, but nothing may be judged: a wave stepped past its window now
    // would raise a MISS into a run that already has a frozen result.
    if (this.runComplete) return;

    simulation.advanceTo(this.audio.clock.now());

    // Published before the events are drained, because draining consumes them.
    // Levels only — the moments go out below, from the same events the runtime
    // already reads, so the queue is still read exactly once.
    this.visualSource.sample(simulation.state.songTime, simulation.state);
    this.profiler.sample(this.analyser?.current ?? SILENT_LEVELS, 1 / 60);

    // A combo *break* is not a signal the bus carries: `NOTE_MISS` fires for
    // every miss, but ending a streak of forty is a different event from missing
    // the first note of a song, and only the runtime can tell them apart.
    const combo = simulation.state.combo;
    if (combo === 0 && this.lastCombo >= 2) {
      this.recorder.recordEvent({
        time: simulation.state.songTime,
        type: 'COMBO_BREAK',
        combo: this.lastCombo,
      });
      this.visualEngine.registerComboBreak();
      this.visualBus.emit({
        name: 'CUSTOM_EVENT',
        time: simulation.state.songTime,
        strength: Math.min(1, this.lastCombo / 40),
        detail: { kind: 'comboBreak', brokenAt: this.lastCombo },
      });
      const decision = this.switchDirector.handleComboBreak();
      if (decision !== null) this.applySwitchSelection();
    }
    this.lastCombo = combo;

    // AUTO chooses its recipe once it has heard enough of the song.
    if (this.autoRecipePending && this.profiler.ready) {
      this.autoRecipePending = false;
      this.applyVisualRecipe(
        autoRecipe({
          seed: this.visualEngine.activeRecipe?.seed ?? randomSeed(),
          available: this.visualEngine.diagnostics().available,
          profile: this.profiler.profile,
          maxLayers: 3,
        }),
      );
      this.refreshVisualPanel();
    }

    // Always drained, so the queue cannot grow unbounded, and always scanned:
    // a miss is raised here rather than by the player, so this is the only
    // place it can be noticed.
    const events = simulation.drainEvents();
    // Handed to the art engine as well as to the HUD. Same array, one drain:
    // the visual side is another reader of what already happened, never a second
    // consumer that could change how many times the queue is emptied.
    this.visualSource.publish(events);
    this.recordRhythmEvents(events, simulation.state);

    for (const event of events) {
      if (event.type === 'waveResolved') {
        // Taps were already published from the input handler with no frame of
        // delay; this catches the ones the simulation raised by itself.
        if (!event.tapped) this.publishJudgement(event);
        continue;
      }
      if (
        event.type === 'waveSpawned' &&
        this.spawnAccuracy.length < BridgeSession.SPAWN_SAMPLES
      ) {
        this.spawnAccuracy.push({
          eventIndex: event.eventIndex,
          chartTime: event.chartTime,
          songTime: event.songTime,
          deltaMs: (event.songTime - event.chartTime) * 1000,
        });
      }
    }

    binder.sync(simulation.state);

    // Checked after the sync, so the frame that ends the run is one where the
    // field has already been drawn empty.
    if (this.stage === 'playing' && !this.runComplete) {
      const complete = isRunComplete({
        state: simulation.state,
        songTime: simulation.state.songTime,
        endGraceSeconds: Math.max(0, this.pack?.manifest.endGraceSeconds ?? 3),
        lastEventTime: this.lastEventTime,
      });
      if (complete) this.finishRun();
    }
  }

  /** Reads cover art if the pack carries one. Absent covers are normal, not errors. */
  private async loadCover(pack: LoadedSongPack): Promise<void> {
    const epoch = this.packEpoch;
    const bytes = copyCoverArrayBuffer(pack);
    if (bytes === null) {
      this.panel.setCover(null);
      return;
    }
    try {
      const bitmap = await createImageBitmap(new Blob([bytes], { type: 'image/png' }));
      if (this.disposed || epoch !== this.packEpoch) { bitmap.close(); return; }
      this.cover = bitmap;
      this.panel.setCover(this.cover);
    } catch (cause) {
      // The pack is still perfectly playable without its artwork.
      this.panel.setCover(null);
      console.warn('[BridgeSession] cover could not be decoded', cause);
    }
  }

  /**
   * Updates the position read-out from `MusicClock`.
   *
   * Deliberately on a timer rather than the render loop: it is evidence that the
   * audio clock runs on its own, independent of whether frames are being drawn.
   */
  private startPositionTicker(): void {
    if (this.positionHandle !== 0) return;
    this.positionHandle = window.setInterval(() => {
      this.gameplayDiagnosticsPanel?.update(this.gameplayDiagnostics());
      this.checkGameplayProgress();
      this.panel.setPosition(this.audio.clock.elapsed());
      // Read, never counted: the simulation already maintains this, and
      // recounting the pool ten times a second to display it would be the view
      // deciding something the simulation owns.
      this.panel.setActiveWaves(this.simulation?.state.activeWaves ?? 0);
    }, 100);
  }

  private reportMirror(): void {
    const mirror = this.prototype.mirrors.diagnostics();
    if (mirror.status === 'unavailable') {
      // Shard counts and the reason a music video was skipped are for whoever
      // is debugging the video pipeline, not for the person playing.
      if (this.debug) this.panel.setNote(`Mirror Drift off — ${mirror.reason ?? 'no MV'}`);
      return;
    }
    const file = mirror.url === null ? '' : decodeURIComponent(mirror.url.split('/').pop() ?? '');
    if (this.debug) this.panel.setNote(`MV ${file} · ${mirror.playingCount}/${mirror.shardCount} shards`);
  }

  diagnostics(): BridgeDiagnostics {
    const buffer = this.track?.buffer;
    const eventsByDifficulty: Record<string, number> = {};
    if (this.pack !== null) {
      for (const name of ['easy', 'normal', 'hard'] as const) {
        eventsByDifficulty[name] = this.pack.charts[name].events.length;
      }
    }
    return {
      stage: this.stage,
      gameplay: this.gameplayDiagnostics(),
      rendering: this.prototype.diagnostics(),
      runtimeMode: this.options.runtimeMode ?? 'web',
      demo: { active: this.demoActive, ...this.demoConfig },
      manifest: this.pack?.manifest ?? null,
      difficulty: this.difficulty,
      chartEvents: this.chart?.meta.eventCount ?? 0,
      eventsByDifficulty,
      audio: {
        state: this.audio.state,
        durationSeconds: buffer?.duration ?? 0,
        sampleRate: buffer?.sampleRate ?? 0,
        channels: buffer?.numberOfChannels ?? 0,
        positionSeconds: this.audio.clock.elapsed(),
        outputLatencyMs: this.audio.clock.outputLatency * 1000,
        scheduledStart: this.track?.scheduledStart ?? null,
      },
      cover:
        this.cover === null
          ? { present: false, width: 0, height: 0 }
          : { present: true, width: this.cover.width, height: this.cover.height },
      mirror: this.prototype.mirrors.diagnostics(),
      startSkewMs: this.startSkewMs,
      simulation: this.simulation === null ? null : snapshot(this.simulation.state),
      spawnAccuracy: this.spawnAccuracy,
      waveSpeed: this.currentWaveSpeed,
      settings: this.audioSettings,
      sfx: this.sfxPlayer.report(),
      creation: {
        available: this.creation !== null,
        phase: this.creation?.phase ?? null,
        packSource: this.packSource,
      },
      library: {
        songs: this.library.entries.length,
        selectedPackId: this.select.selected,
        loadedPackId: this.currentPackId,
        storedBytes: this.library.entries.reduce((total, entry) => total + entryBytes(entry), 0),
        entries: this.library.entries.map((entry) => ({
          packId: entry.packId,
          displayName: entry.displayName,
          hasVideo: entry.hasVideo,
          hasCover: entry.hasCover,
          bytes: entryBytes(entry),
        })),
        videoUrls: {
          created: this.createdVideoUrls,
          revoked: this.revokedVideoUrls,
          // Must be 0 or 1 at all times: one song, one video, one URL.
          live: this.createdVideoUrls - this.revokedVideoUrls,
        },
      },
      flow: {
        screen: this.screen,
        runComplete: this.runComplete,
        lastEventTime: this.lastEventTime,
        result: this.lastResult,
      },
      visual: {
        interactionMode: this.interactionMode,
        engine: this.visualEngine.diagnostics(),
        switchDirector: this.switchDirector.diagnostics(),
        audioProfile: this.profiler.profile,
      },
      replay: {
        recorder: this.recorder.diagnostics(),
        replaying: this.replaying,
        playback: this.replayPlayer.progress(),
        available: this.lastReplay !== null,
        stored:
          this.lastReplay === null
            ? null
            : {
                song: this.lastReplay.song.displayName,
                packId: this.lastReplay.song.packId,
                chartHash: this.lastReplay.song.chartHash,
                inputs: this.lastReplay.timeline.playerInputs.length,
                offBeatInputs: this.lastReplay.timeline.playerInputs.filter((i) => !i.judged).length,
                events: this.lastReplay.timeline.rhythmEvents.length,
                interactionMode: this.lastReplay.performance.interactionMode,
                seed: this.lastReplay.performance.visualSeed,
                result: this.lastReplay.result,
                bytes: serializeReplay(this.lastReplay).length,
              },
        fidelity: this.replayComparison,
      },
      judge: {
        settings: this.judgeSettings,
        resolutions: this.resolutions.length,
        recent: this.resolutions.slice(-8).map((entry) => ({
          eventIndex: entry.eventIndex,
          grade: entry.grade,
          timingOffsetMs: Number(entry.timingOffsetMs.toFixed(1)),
          timingDirection: entry.timingDirection,
          tapped: entry.tapped,
        })),
      },
      error: this.error,
    };
  }

  /**
   * Resolves a tap given in *world* coordinates.
   *
   * Exposed for the automated browser check, which has no way to synthesise a
   * trusted pointer event. It enters the same path a real tap does — same hit
   * test, same clock read by the caller, same judging — so what it exercises is
   * the shipping behaviour rather than a parallel one.
   */
  tapWorld(worldX: number, worldY: number, songTime = this.audio.clock.now()): WaveResolvedEvent | null {
    return this.handleTap(worldX, worldY, songTime);
  }

  /** Converts client pixels to the gameplay plane, for the same purpose. */
  clientToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
    const point = this.pointer.toWorld(clientX, clientY);
    return point === null ? null : { x: point.x, y: point.y };
  }

  dispose(): void {
    this.advancedSettings.dispose();
    this.disposed = true;
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.gameplayStatus.dispose();
    this.gameplayDiagnosticsPanel?.dispose();
    if (this.positionHandle !== 0) window.clearInterval(this.positionHandle);
    this.positionHandle = 0;
    if (this.onPointerDown !== null) {
      this.options.canvas.removeEventListener('pointerdown', this.onPointerDown);
      this.onPointerDown = null;
    }
    if (this.artHandle !== 0) window.clearInterval(this.artHandle);
    this.artHandle = 0;
    this.prototype.setVisualCallback(null);
    this.visualPanel.dispose();
    this.visualEngine.dispose();
    this.analyser?.dispose();
    this.analyser = null;
    this.judgePanel.dispose();
    this.settingsPanel.dispose();
    this.createScreen?.dispose();
    this.sfxPlayer.dispose();
    this.prototype.setFrameCallback(null);
    this.binder?.clear();
    this.track?.dispose();
    this.cover?.close();
    this.panel.dispose();
    this.select.dispose();
    this.title.dispose();
    this.demoGuide.dispose();
    this.result.dispose();
    if (this.onKeyDown !== null) window.removeEventListener('keydown', this.onKeyDown);
    this.onKeyDown = null;
    this.backButton.remove();
    this.titleButton.remove();
    this.library.close();
    this.prototype.dispose();
    this.audio.dispose();
  }
}
